import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DocPlacement from '../components/DocPlacement';
import SheetColumns from './DocImportColumns';
import { getCsrfToken } from '../utils/auth';
import BlockTreePreview from '../components/BlockTreePreview';
import { SearchSelect } from './review/reviewUi';
import '../styles/DocReview.css';
import '../styles/DocImport.css';

// Import from Documents (design/DESIGN_DOC_IMPORT.md) — an AI Studio mode.
// 1 Documents → 2 Review items → 3 Review links → 4 Create. Nothing is written to Orcanos until
// Create; every item keeps the quote it came from. All classes prefixed `di-` (Critical Note #15).

const jsonHeaders = () => ({ 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() });
const BUSY = ['surveying', 'extracting', 'creating'];   // the server works on; the page only polls
const DONE = ['created', 'linked', 'existing', 'updated'];
const AI_KINDS = ['rewritten', 'generated'];
// Where an item came from — shown per level before Create and in the downloaded list.
const ORIGIN = { row: 'Excel row (as written)', quoted: 'Quoted from a document by the AI', written: 'Written by the AI', derived: 'Derived by the AI from another level' };
const ORIGIN_SHORT = { row: 'Excel rows', quoted: 'quoted', written: 'written by AI', derived: 'derived by AI' };
const origin = i => (i.sources?.[0]?.derived ? 'derived'
  : /^One row of the sheet/.test(i.level_reason || '') ? 'row'
    : AI_KINDS.includes(i.kind) ? 'written' : 'quoted');

async function api(url, opts = {}) {
  const res = await fetch(url, { credentials: 'include', ...opts });
  let body = null;
  try { body = await res.json(); } catch (e) { body = null; }
  if (!res.ok) throw new Error((body && (body.detail || body.error)) || `Request failed (${res.status})`);
  return body;
}

const send = (url, method, data) => api(url, { method, headers: jsonHeaders(), body: JSON.stringify(data || {}) });

// A CSV the person keeps (the item list with old ids, the old-id table) — Excel opens it (BOM = UTF-8).
function downloadCsv(name, rows) {
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const blob = new Blob(['\ufeff' + rows.map(r => r.map(esc).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

function fmtWhen(s) {
  if (!s) return '';
  return `${String(s).replace('T', ' ').slice(0, 16)} UTC`;
}

// ------------------------------------------------------------------ list of imports
function ImportList({ setupId }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromDhf = params.get('from') === 'dhf';         // opened from the DHF page: back goes there
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api(`/api/build/${setupId}/import`).then(d => setRows(d.imports)).catch(e => setError(e.message));
  }, [setupId]);
  useEffect(() => { load(); }, [load]);
  const running = (rows || []).some(r => BUSY.includes(r.status));
  useEffect(() => {                                       // a run working on the server: keep its status fresh
    if (!running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);

  const start = async () => {
    setBusy(true);
    try {
      const d = await send(`/api/build/${setupId}/import`, 'POST', {});
      navigate(`/trace/build/${setupId}/import/${d.id}${fromDhf ? '?from=dhf' : ''}`);
    } catch (e) { setError(e.message); setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this import record? Items it already created stay in Orcanos.')) return;
    try {
      await api(`/api/build/${setupId}/import/${id}`, { method: 'DELETE', headers: jsonHeaders() });
      setRows(r => r.filter(x => x.id !== id));
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="di-page">
      <div className="di-head">
        <div className="di-head-title">
          <button type="button" className="di-back" onClick={() => navigate(fromDhf ? `/dhf/${setupId}` : `/trace/build/${setupId}`)}>
            {fromDhf ? '← DHF' : '← AI Studio'}</button>
          <h1>Import from documents</h1>
        </div>
        <button type="button" className="di-btn di-btn-p" onClick={start} disabled={busy}>+ New import</button>
      </div>
      <p className="di-lead">Turn the customer's documents into requirements, tests and traces in this panel.
        The AI takes each item out of the files with the quote it came from, checks what is already in
        Orcanos and proposes the links. You review everything; approved items are created as drafts.</p>
      {error && <div className="di-error">{error}</div>}
      <div className="di-card">
        <div className="di-card-title">Recent imports</div>
        {rows === null ? <div className="di-muted">Loading…</div> : rows.length === 0 ? (
          <div className="di-muted">No imports yet for this panel.</div>
        ) : (
          <table className="di-table">
            <thead><tr><th>Import</th><th>Status</th><th>Items</th><th>By</th><th>Updated</th><th /></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td><button type="button" className="di-link" onClick={() => navigate(`/trace/build/${setupId}/import/${r.id}${fromDhf ? '?from=dhf' : ''}`)}>
                    {r.name || `Import of ${fmtWhen(r.created_at)}`}</button></td>
                  <td>{r.status === 'done' && r.error   // a restart mid-Create lands on "done" (reset_interrupted)
                    ? <span className="di-status di-status-error" title={r.error}>stopped — press Create</span>
                    : <span className={`di-status di-status-${r.status}`}>{r.status}</span>}</td>
                  <td>{r.counts?.items ?? '—'}</td>
                  <td>{r.user_name}</td>
                  <td>{fmtWhen(r.updated_at)}</td>
                  <td>{!BUSY.includes(r.status) && <button type="button" className="di-btn di-btn-s" onClick={() => remove(r.id)}>Delete</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ step 1: documents
const ROLES = [['content', 'Requirements / tests'], ['trace', 'Trace table only'], ['both', 'Both']];

// The first look's QUESTIONS (2026-10-27): built by the server from the file's guide — where the old
// ID is, which column names the paragraph, which column holds trace ids and of which level, how to
// group into paragraphs — plus the AI's own questions, which also take the person's words. The AI's
// guess is preselected and marked until the person picks; each pick is saved at once.
// Numbered across the whole file card (2026-10-27): the file's own questions first, then each sheet's.
function Question({ q, n, save, locked }) {
  const [own, setOwn] = useState(null);
  const custom = q.free && q.answered && !q.options.some(o => o.value === q.value);
  const pick = v => save({ answers: { [q.id]: v } });
  return (
    <div className={`di-q ${q.answered ? 'done' : ''}`}>
      <span className="di-q-n" aria-hidden="true">{q.answered ? '✓' : n}</span>
      <div className="di-q-body">
        <div className="di-q-text">{q.text}
          {!q.answered && q.value && <span className="di-chip di-chip-purple">AI's guess — confirm or change</span>}</div>
        {q.why && <div className="di-muted di-small">{q.why}</div>}
        <div className="di-q-opts">
          {q.options.length > 5 ? (
            <>
              <select value={q.value} disabled={locked} aria-label={q.text} onChange={e => pick(e.target.value)}>
                {!q.value && <option value="">Choose…</option>}
                {q.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {!q.answered && q.value && <button type="button" className="di-btn di-btn-s" disabled={locked} onClick={() => pick(q.value)}>Confirm</button>}
            </>
          ) : (
            <div className="di-seg" role="group" aria-label={q.text}>
              {q.options.map(o => (
                <button key={o.value} type="button" disabled={locked} className={q.value === o.value ? 'on' : ''}
                  onClick={() => pick(o.value)}>{o.label}</button>
              ))}
            </div>
          )}
          {q.free && (
            <input className="di-q-own" placeholder="Or your own answer…" disabled={locked}
              value={own ?? (custom ? q.value : '')} onChange={e => setOwn(e.target.value)}
              onBlur={e => { const v = e.target.value.trim(); if (v && v !== q.value) pick(v); }} />
          )}
        </div>
      </div>
    </div>
  );
}

const sheetName = t => (t.anchor || '').match(/^sheet '(.*)'$/)?.[1];

// "What I found" under a file: the AI's first look (summary, levels, which table is a trace
// table) and the person's corrections — saved at once, used by the next Read documents.
// Laid out 2026-10-27 (user: "too loaded"): one section for the whole file, then one section
// per sheet / table, each with its own questions and a Skip switch — a skipped sheet is not read.
function FileGuide({ f, levels, base, reload, setError, locked }) {
  const g = f.guide || {};
  const tables = g.tables || [];
  const [note, setNote] = useState(g.note || '');
  useEffect(() => { setNote(g.note || ''); }, [g.note]);
  const lvl = Object.fromEntries(levels.map(l => [l.block_id, l]));
  const edges = levels.filter(l => lvl[l.parent_block_id]);
  const isTable = /\.(docx|xlsx|xlsm)$/i.test(f.name);
  const isExcel = /\.(xlsx|xlsm)$/i.test(f.name);
  const looked = g.surveyed || Object.keys(g.answers || {}).length > 0;
  const qs = looked ? (f.questions || []) : [];
  const open = qs.filter(q => !q.answered).length;
  const fileQs = qs.filter(q => !q.table || !tables.some(t => t.id === q.table));
  const tableQs = id => qs.filter(q => q.table === id);
  let n = 0;                                                  // question numbers run across the card
  const numbered = list => list.map(q => ({ q, n: ++n }));
  const fileList = numbered(fileQs);
  const tableLists = Object.fromEntries(tables.map(t => [t.id, t.skipped ? [] : numbered(tableQs(t.id))]));
  const save = async (patch) => {
    try { await send(`${base}/files/${f.id}`, 'PATCH', patch); reload(); } catch (e) { setError(e.message); }
  };
  const saveTable = (t, change) => {
    const m = { id: t.id, trace: t.trace, parent_block: t.parent_block, child_block: t.child_block,
      parent_col: t.parent_col, child_col: t.child_col, ...change };
    if (m.trace && !m.child_block && edges[0]) {            // first tick: the first edge, columns 1 and 2
      m.child_block = edges[0].block_id;
      m.parent_block = edges[0].parent_block_id;
    }
    if (m.trace && !m.parent_col) m.parent_col = 1;
    if (m.trace && !m.child_col) m.child_col = Math.min(2, t.header.length);
    save({ tables: [m] });
  };
  const cols = t => t.header.map((h, i) => <option key={i} value={i + 1}>{`${i + 1}. ${h || '(no header)'}`}</option>);
  const used = tables.filter(t => !t.skipped).length;
  return (
    <details className="di-guide" open={!!g.surveyed || tables.some(t => t.trace) || Object.keys(g.answers || {}).length > 0}>
      <summary>
        <span className="di-guide-title">What I found</span>
        {g.surveyed ? <span className="di-muted di-small">{g.summary}</span>
          : <span className="di-muted di-small">{tables.length ? `${tables.length} ${tables.length === 1 ? 'table' : 'tables'} — not looked at by the AI yet` : 'Not looked at yet'}</span>}
        {g.edited && <span className="di-chip">Corrected by you</span>}
        {looked && (open > 0 ? <span className="di-chip di-chip-amber">{open} to answer</span>
          : qs.length > 0 && <span className="di-chip di-chip-ok">All answered</span>)}
      </summary>

      <section className="di-sec">
        <div className="di-sec-head"><span className="di-label">The whole file</span></div>
        <div className="di-guide-row">
          <span className="di-small">This file holds</span>
          <div className="di-seg" role="group" aria-label="What this file holds">
            {ROLES.map(([k, t]) => (
              <button key={k} type="button" className={(g.role || 'content') === k ? 'on' : ''} disabled={locked}
                onClick={() => save({ role: k })}
                title={k === 'trace' ? 'Only ids linked to ids — the AI does not read it for items; its trace tables become links' : ''}>{t}</button>
            ))}
          </div>
        </div>
        {!looked && <div className="di-muted di-small">The AI looks at the start of the file and its tables first, then asks you a few questions here — the old ID, the paragraphs, the trace columns.</div>}
        {fileList.map(({ q, n: k }) => <Question key={q.id} q={q} n={k} save={save} locked={locked} />)}
        {g.surveyed && g.levels?.length > 0 && (
          <div className="di-expect">
            <div className="di-small di-muted">The AI expects to find, roughly:</div>
            <table className="di-expect-table"><tbody>
              {g.levels.map(l => (
                <tr key={l.block_id}><td className="di-expect-lv">{l.label}</td><td className="di-expect-n">~{l.about}</td>
                  <td className="di-muted">{l.note}</td></tr>))}
            </tbody></table>
          </div>
        )}
        {!isTable && <div className="di-muted di-small">Trace tables are read as tables only from Word and Excel files. For a trace table in a PDF, save it as Word or Excel, or describe it in the note.</div>}
        <label className="di-field">
          <span>More instructions for the AI <span className="di-muted">— e.g. “Section 7 is history, skip it”, “rows with Status = Obsolete are not requirements”</span></span>
          <textarea rows={2} value={note} disabled={locked} onChange={e => setNote(e.target.value)}
            onBlur={() => { if (note.trim() !== (g.note || '')) save({ note }); }} />
        </label>
      </section>

      {tables.length > 0 && (
        <div className="di-sec-head di-sec-list">
          <span className="di-label">{isExcel ? 'Sheets' : 'Tables'}</span>
          <span className="di-muted di-small">{used} of {tables.length} read{used < tables.length ? ` · ${tables.length - used} skipped` : ''}</span>
        </div>
      )}
      {tables.map(t => {
        const sheet = sheetName(t);
        const showCols = isExcel && !(t.trace && t.trace_from === 'table') && t.rows >= 3;
        return (
          <section key={t.id} className={`di-sec di-sheet ${t.skipped ? 'skipped' : ''}`}>
            <div className="di-sheet-head">
              <div className="di-sheet-title">
                <b>{sheet ?? t.anchor}</b>
                <span className="di-muted di-small">{t.rows} rows{t.header?.length ? ` · ${t.header.filter(Boolean).slice(0, 5).join(', ')}` : ''}</span>
              </div>
              <div className="di-seg" role="group" aria-label={`Read ${sheet ?? t.anchor}`}>
                <button type="button" className={!t.skipped ? 'on' : ''} disabled={locked}
                  onClick={() => t.skipped && save({ skip_tables: { [t.id]: false } })}>Read</button>
                <button type="button" className={t.skipped ? 'on' : ''} disabled={locked}
                  title="Nothing reads this sheet — no items, no links, no questions"
                  onClick={() => !t.skipped && save({ skip_tables: { [t.id]: true } })}>Skip</button>
              </div>
            </div>
            {t.skipped ? <div className="di-muted di-small">Skipped — not read.{t.auto_skipped ? ` ${t.auto_skipped}.` : ''}</div> : (
              <>
                {t.reason && <div className="di-muted di-small">{t.reason}</div>}
                {tableLists[t.id].map(({ q, n: k }) => <Question key={q.id} q={q} n={k} save={save} locked={locked} />)}
                <div className={`di-ttable ${t.trace ? 'on' : ''}`}>
                  <label className="di-check di-small">
                    <input type="checkbox" checked={!!t.trace} disabled={locked || !edges.length}
                      onChange={e => saveTable(t, { trace: e.target.checked })} />
                    This is a trace table <span className="di-muted">— ids linked to ids; its rows become links</span>
                  </label>
                  {t.trace && (
                    <div className="di-ttable-map">
                      <label className="di-field"><span>Links</span>
                        <select value={t.child_block} disabled={locked}
                          onChange={e => saveTable(t, { child_block: e.target.value, parent_block: lvl[e.target.value].parent_block_id })}>
                          {edges.map(l => <option key={l.block_id} value={l.block_id}>{lvl[l.parent_block_id].label} → {l.label}</option>)}
                        </select>
                      </label>
                      <label className="di-field"><span>{lvl[t.parent_block]?.label} ids in column</span>
                        <select value={t.parent_col} disabled={locked} onChange={e => saveTable(t, { parent_col: Number(e.target.value) })}>{cols(t)}</select>
                      </label>
                      <label className="di-field"><span>{lvl[t.child_block]?.label} ids in column</span>
                        <select value={t.child_col} disabled={locked} onChange={e => saveTable(t, { child_col: Number(e.target.value) })}>{cols(t)}</select>
                      </label>
                    </div>
                  )}
                  {t.trace && t.sample?.length > 0 && (
                    <div className="di-muted di-small">First rows: {t.sample.slice(0, 3).map(r => `${r[t.parent_col - 1] || '—'} → ${r[t.child_col - 1] || '—'}`).join(' · ')}</div>
                  )}
                </div>
                {showCols && (
                  <details className="di-cols-wrap" open={!!t.mapped || !!t.ai_columns?.length}>
                    <summary>
                      <span className="di-guide-title">Columns</span>
                      {t.mapped
                        ? <span className="di-chip di-chip-ok">{t.rows_items ? 'Mapped · each row is one item' : 'Mapped'}</span>
                        : <span className="di-muted di-small">Say which columns matter — the AI then reads only those, and list values are matched to Orcanos</span>}
                    </summary>
                    <SheetColumns base={base} fileId={f.id} table={t} locked={locked} setError={setError} onSaved={reload} />
                  </details>
                )}
              </>
            )}
          </section>
        );
      })}
    </details>
  );
}

function FileRow({ f, onFormal, onRemove, locked, guide }) {
  return (
    <div className="di-file-wrap">
    <div className="di-file">
      <span className="di-file-name">{f.name}</span>
      <span className="di-muted di-small">{f.pages} {f.pages === 1 ? 'page' : 'pages'}</span>
      {f.status === 'ocr' && (
        <span className={`di-chip ${f.error ? 'di-chip-red' : 'di-chip-amber'}`} title={f.error || ''}>
          {f.error ? 'Scanned — could not be read' : 'Scanned — the AI will read the pages'}</span>
      )}
      <div className="di-seg" role="group" aria-label="Kind of document">
        <button type="button" className={f.formal ? 'on' : ''} disabled={locked} onClick={() => onFormal(f, true)}
          title="Already has numbered requirements or tests — they are quoted as written">Formal</button>
        <button type="button" className={!f.formal ? 'on' : ''} disabled={locked} onClick={() => onFormal(f, false)}
          title="Notes, emails, minutes, logs — the AI writes formal statements from them">Informal</button>
      </div>
      {!locked && <button type="button" className="di-x" aria-label={`Remove ${f.name}`} onClick={() => onRemove(f)}>×</button>}
    </div>
    {guide && <FileGuide f={f} locked={locked} {...guide} />}
    </div>
  );
}

// A level folds to its header (2026-10-27) — remembered per browser, per level.
const FOLD_KEY = 'docImport.foldedLevels';
function readFolded() {
  try { return JSON.parse(localStorage.getItem(FOLD_KEY) || '{}') || {}; } catch { return {}; }
}

function LevelFiles({ title, sub, chip, files, blockId, locked, onUpload, onFormal, onRemove, dashed, children, guide }) {
  const input = useRef(null);
  const [folded, setFolded] = useState(() => !!readFolded()[blockId]);
  const toggle = () => {
    const next = !folded;
    setFolded(next);
    try { localStorage.setItem(FOLD_KEY, JSON.stringify({ ...readFolded(), [blockId]: next })); } catch { /* private window */ }
  };
  return (
    <div className={`di-card ${dashed ? 'di-card-dashed' : ''} ${folded ? 'di-card-folded' : ''}`}>
      <div className="di-card-row">
        <button type="button" className="di-level-head di-level-toggle" onClick={toggle} aria-expanded={!folded}
          title={folded ? 'Show this level' : 'Fold this level'}>
          <span className={`di-fold ${folded ? '' : 'open'}`} aria-hidden="true">▸</span>
          {chip && <span className="di-chip di-chip-level">{chip}</span>}
          <div>
            <div className="di-level-title">{title}</div>
            {sub && <div className="di-muted di-small">{sub}{folded && ` · ${files.length} ${files.length === 1 ? 'file' : 'files'}`}</div>}
          </div>
        </button>
        {!locked && (
          <>
            <button type="button" className="di-btn" onClick={() => input.current?.click()}>+ Add files</button>
            <input ref={input} type="file" multiple hidden accept=".docx,.pdf,.xlsx,.xlsm,.txt,.md"
              onChange={e => { onUpload(blockId, Array.from(e.target.files || [])); e.target.value = ''; }} />
          </>
        )}
      </div>
      {!folded && files.map(f => <FileRow key={f.id} f={f} locked={locked} onFormal={onFormal} onRemove={onRemove} guide={guide} />)}
      {!folded && children}
    </div>
  );
}

// What belongs on a level — sent to the AI so it can tell, say, a user need from a product
// requirement when one document feeds both. Saved on blur; Reset brings back the built-in text.
function MeaningField({ lv, locked, onSave }) {
  const [text, setText] = useState(lv.meaning || '');
  useEffect(() => { setText(lv.meaning || ''); }, [lv.meaning]);
  const changed = (lv.meaning || '') !== (lv.default_meaning || '');
  return (
    <label className="di-field di-meaning">
      <span>What belongs on this level <span className="di-muted">— the AI sorts items by this</span></span>
      <textarea rows={2} value={text} disabled={locked}
        placeholder={`Describe a ${lv.label} item, e.g. what it states and what it leaves to the level below`}
        onChange={e => setText(e.target.value)} onBlur={() => onSave(lv, text)} />
      {changed && !locked && lv.default_meaning && (
        <button type="button" className="di-link di-small" onClick={() => { setText(lv.default_meaning); onSave(lv, lv.default_meaning); }}>
          Reset to the built-in text</button>
      )}
    </label>
  );
}

function DocumentsStep({ data, base, reload, setError }) {
  const { run, files, levels, estimate, counts } = data;
  const locked = BUSY.includes(run.status);
  const [uploading, setUploading] = useState([]);
  const [notes, setNotes] = useState([]);
  const guide = { levels, base, reload, setError };

  const upload = async (blockId, list) => {
    for (const file of list) {
      setUploading(u => [...u, file.name]);
      try {
        const form = new FormData();
        form.append('file', file);
        form.append('block_id', blockId);
        form.append('formal', 'auto');
        const d = await api(`${base}/files`, { method: 'POST', headers: { 'X-CSRF-Token': getCsrfToken() }, body: form });
        if (d.already_imported) setNotes(n => [...n, `${file.name} was already imported into this panel on ${fmtWhen(d.already_imported.at)}.`]);
        if (d.earlier_version) setNotes(n => [...n, `An earlier version of ${file.name} was imported on ${fmtWhen(d.earlier_version.at)}. Items already in Orcanos come back as “Exists” — choose “Update it in Orcanos” on the ones whose wording changed.`]);
      } catch (e) { setError(e.message); }
      setUploading(u => u.filter(n => n !== file.name));
      reload();
    }
  };
  const setFormal = async (f, formal) => {
    try { await send(`${base}/files/${f.id}`, 'PATCH', { formal }); reload(); } catch (e) { setError(e.message); }
  };
  const removeFile = async (f) => {
    try { await api(`${base}/files/${f.id}`, { method: 'DELETE', headers: jsonHeaders() }); reload(); } catch (e) { setError(e.message); }
  };
  const extract = async () => {
    // First understand each file and ask (the user's flow, 2026-10-27): files nobody looked at are
    // looked at now, and the import waits for the answers.
    if (files.some(f => !f.guide?.surveyed && f.status !== 'ocr' && !Object.keys(f.guide?.answers || {}).length)) {
      await survey(false);
      setNotes(n => [...n, 'The AI is looking at the files and will ask a few questions under each one. Answer them (or keep its guesses), then press Read documents again.']);
      return;
    }
    const open = files.reduce((n, f) => n + (f.questions || []).filter(q => !q.answered).length, 0);
    if (open && !window.confirm(`${open} ${open === 1 ? 'question is' : 'questions are'} not answered yet. Use the AI's guesses for them?`)) return;
    if (counts.items > 0 && !window.confirm('Reading the documents again replaces the proposed items, links and your decisions on them. Continue?')) return;
    try { await send(`${base}/extract`, 'POST'); reload(); } catch (e) { setError(e.message); }
  };
  const stop = async () => { try { await send(`${base}/stop`, 'POST'); } catch (e) { setError(e.message); } };
  const notLooked = files.filter(f => !f.guide?.surveyed && f.status !== 'ocr');
  const survey = async (all) => {
    if (all && files.some(f => f.guide?.edited) && !window.confirm('Looking again replaces what the AI found and your corrections (your notes are kept). Continue?')) return;
    // Runs on the server (2026-10-27): the run turns "surveying" and the page polls; leaving the
    // page or the tab does not stop it. Its result waits on the run (options.survey).
    try { await send(`${base}/survey`, 'POST', { file_ids: all ? files.map(f => f.id) : [] }); } catch (e) { setError(e.message); }
    reload();
  };
  const looked = run.options?.survey || {};

  const byBlock = id => files.filter(f => f.block_id === id);
  const parentLabel = lv => levels.find(p => p.block_id === lv.parent_block_id)?.label;
  const derive = run.options?.derive || {};
  const deriveUp = run.options?.derive_up || {};
  const setDerive = async (blockId, on, key = 'derive') => {
    try { await send(`${base}/options`, 'PATCH', { [key]: { [blockId]: on } }); reload(); } catch (e) { setError(e.message); }
  };
  const childLabels = lv => levels.filter(c => c.parent_block_id === lv.block_id).map(c => c.label).join(' / ');
  const setMeaning = async (lv, text) => {
    if (text.trim() === (lv.meaning || '').trim()) return;
    try { await send(`${base}/options`, 'PATCH', { meanings: { [lv.block_id]: text.trim() } }); reload(); } catch (e) { setError(e.message); }
  };

  return (
    <div className="di-two">
      <div className="di-main">
        <p className="di-lead">Add the files for each level of the panel — a level can take several. Mark a file
          <b> Formal</b> when it already has numbered requirements or tests, or <b>Informal</b> when the AI has to write
          them from notes, emails or prose. The first choice is a guess from the text; change it if it is wrong.</p>
        {notes.map((n, i) => <div key={i} className="di-note">{n}</div>)}
        {levels.map(lv => (
          <LevelFiles key={lv.block_id} chip={`L${lv.depth}`} title={lv.label} blockId={lv.block_id}
            sub={lv.parent_block_id ? `traces to ${parentLabel(lv)}` : 'top level'}
            files={byBlock(lv.block_id)} locked={locked} onUpload={upload} onFormal={setFormal} onRemove={removeFile} guide={guide}>
            <MeaningField lv={lv} locked={locked} onSave={setMeaning} />
            {lv.parent_block_id && (
              <label className="di-check di-small">
                <input type="checkbox" checked={!!derive[lv.block_id]} disabled={locked}
                  onChange={e => setDerive(lv.block_id, e.target.checked)} />
                Also derive {lv.label} items from the {parentLabel(lv)} documents
                <span className="di-muted">— for where nobody wrote them; each is marked Rewritten by AI</span>
              </label>
            )}
            {childLabels(lv) && (
              <label className="di-check di-small">
                <input type="checkbox" checked={!!deriveUp[lv.block_id]} disabled={locked}
                  onChange={e => setDerive(lv.block_id, e.target.checked, 'derive_up')} />
                Also derive {lv.label} items from the {childLabels(lv)} documents
                <span className="di-muted">— the needs those items serve, when nobody wrote them; each is marked Rewritten by AI and linked to the items it came from</span>
              </label>
            )}
          </LevelFiles>
        ))}
        <LevelFiles dashed title="Mixed documents" blockId=""
          sub="Not sure where a file belongs? Add it here — the AI puts each finding on a level."
          files={byBlock('')} locked={locked} onUpload={upload} onFormal={setFormal} onRemove={removeFile} guide={guide} />
        {uploading.length > 0 && <div className="di-muted">Reading {uploading.join(', ')}…</div>}
        <div className="di-muted di-small">Test Run levels take no files — a run comes from running a test case.</div>
      </div>
      <aside className="di-side">
        <div className="di-card-title">Before you start</div>
        <div className="di-stats">
          <div><b>{estimate.files}</b><span>{estimate.files === 1 ? 'file' : 'files'} added</span>
            <span className="di-small">up to {estimate.limits.files} per import</span></div>
          <div><b>{estimate.pages}</b><span>{estimate.pages === 1 ? 'page' : 'pages'} in them</span>
            <span className="di-small">up to {estimate.limits.pages} per import</span></div>
        </div>
        <ol className="di-steps">
          <li>A first look at each file, then a few questions for you: the old ID, the paragraphs, the trace columns</li>
          <li>Take requirements and tests out of each file, with the quote they came from</li>
          <li>Rewrite informal text as a formal statement</li>
          <li>Check each one against what is already in Orcanos</li>
          <li>Propose the links between levels</li>
        </ol>
        <div className="di-note">Nothing is written to Orcanos until you create. Items are created in the type's
          first workflow state (Draft) and go through your normal review and approval.</div>
        <div className="di-muted di-small">About {estimate.calls} AI {estimate.calls === 1 ? 'call' : 'calls'} to read the files, plus a few to match and link.</div>
        {run.error && !locked && <div className="di-error">{run.error}</div>}
        {!locked && (looked.errors?.length > 0 || looked.skipped?.length > 0 || looked.stopped) && (
          <div className="di-warn di-small">
            {looked.stopped && <div>The first look was stopped — press Look and ask me for the rest.</div>}
            {(looked.errors || []).map((e, i) => <div key={i}>{e}</div>)}
            {looked.skipped?.length > 0 && <div>{looked.skipped.join(', ')}: scanned — the AI reads the pages during the import, so there is nothing to look at yet.</div>}
          </div>
        )}
        {locked ? (
          <div className="di-progress-box">
            <div className="di-small">{run.phase || 'Working…'}</div>
            <div className="di-bar"><i style={{ width: `${run.progress || 0}%` }} /></div>
            {/* The server stops after the AI calls already running (up to a minute) — say so, never a dead button. */}
            <button type="button" className="di-btn" onClick={stop} disabled={!!run.stop}>{run.stop ? 'Stopping…' : 'Stop'}</button>
            {run.stop && <div className="di-muted di-small">Stopping — waiting for the AI calls already running to finish. Nothing new is started.</div>}
            <div className="di-muted di-small">You can leave this page or switch tabs — the import keeps running on the server.</div>
          </div>
        ) : (
          <>
            <button type="button" className="di-btn" disabled={!files.length}
              onClick={() => survey(!notLooked.length)}
              title="One short AI call per file on its start and its tables' first rows: what it holds, and questions for you. Answer them below each file.">
              {notLooked.length ? `✨ Look and ask me (${notLooked.length} ${notLooked.length === 1 ? 'file' : 'files'})` : '✨ Look at the files again'}</button>
            {counts.created > 0
              ? <div className="di-note di-small">{counts.created} item(s) of this import are in Orcanos, so the documents are not read again
                  (that would create them twice). To add missing traces, use <b>Re-read trace columns</b> on the Links step.</div>
              : <button type="button" className="di-btn di-btn-p" disabled={!files.length} onClick={extract}>
                  {counts.items > 0 ? 'Read documents again' : 'Read documents'}</button>}
          </>
        )}
      </aside>
    </div>
  );
}

// ------------------------------------------------------------------ step 2: items
const ITEM_FILTERS = [
  ['all', 'All'], ['new', 'New'], ['exists', 'Already in Orcanos'], ['dup', 'Possible duplicate'],
  ['rewritten', 'Rewritten / written by AI'], ['noquote', 'Quote not found'], ['pending', 'Not decided'],
  ['accepted', 'Accepted'], ['rejected', 'Rejected'],
];

function matchesFilter(it, f) {
  if (f === 'all') return it.decision !== 'rejected' || !it.reject_reason;
  if (f === 'rejected') return it.decision === 'rejected';
  if (f === 'pending' || f === 'accepted') return it.decision === f;
  if (f === 'rewritten') return AI_KINDS.includes(it.kind) && it.decision !== 'rejected';
  if (f === 'noquote') return !it.quote_ok && it.decision !== 'rejected';
  return it.match === f && it.decision !== 'rejected';
}

function MatchChip({ it }) {
  if (it.create_status && DONE.includes(it.create_status)) return <span className="di-chip di-chip-ok">In Orcanos · {it.created_key}</span>;
  if (it.match === 'exists') return <span className="di-chip di-chip-purple">Exists · {String(it.match_key).split(' (')[0]}</span>;
  if (it.match === 'update') return <span className="di-chip di-chip-amber">Update · {String(it.match_key).split(' (')[0]}</span>;
  if (it.match === 'dup') return <span className="di-chip di-chip-red">Possible duplicate</span>;
  return <span className="di-chip di-chip-ok">New</span>;
}

function KindChip({ kind }) {
  if (kind === 'rewritten') return <span className="di-chip di-chip-amber">Rewritten by AI</span>;
  if (kind === 'generated') return <span className="di-chip di-chip-amber">Written by AI</span>;
  return <span className="di-chip">Quoted</span>;
}

// Split at the sentence nearest the middle — a first guess the person edits.
function splitGuess(text) {
  const t = (text || '').trim();
  const cuts = [...t.matchAll(/[.;]\s+/g)].map(m => m.index + m[0].length);
  if (!cuts.length) return [t, ''];
  const mid = t.length / 2;
  const at = cuts.reduce((a, b) => (Math.abs(b - mid) < Math.abs(a - mid) ? b : a));
  return [t.slice(0, at).trim(), t.slice(at).trim()];
}

function DecisionChip({ d }) {
  const t = { pending: 'Not decided', accepted: 'Accepted', rejected: 'Rejected' }[d] || d;
  return <span className={`di-dec di-dec-${d}`}>{t}</span>;
}

function ItemPanel({ it, level, levels, base, onSaved, onSplit, setError, locked, paragraphs = [] }) {
  const [title, setTitle] = useState(it.title);
  const [oldId, setOldId] = useState(it.doc_ref || '');
  const [para, setPara] = useState(it.paragraph || '');
  useEffect(() => { setOldId(it.doc_ref || ''); setPara(it.paragraph || ''); }, [it.id, it.doc_ref, it.paragraph]);
  const [text, setText] = useState(it.text);
  const [steps, setSteps] = useState(it.steps || []);
  const [section, setSection] = useState(null);
  const [parts, setParts] = useState(null);       // [{title, text}] while splitting
  const [big, setBig] = useState(false);          // the same panel, opened as a large pop-up for reading/editing
  useEffect(() => {
    if (!big) return undefined;
    const onKey = e => { if (e.key === 'Escape') setBig(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [big]);
  useEffect(() => { setTitle(it.title); setText(it.text); setSteps(it.steps || []); setSection(null); setParts(null); }, [it.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = title !== it.title || text !== it.text || JSON.stringify(steps) !== JSON.stringify(it.steps || []);
  const src = (it.sources || [])[0] || {};
  const written = DONE.includes(it.create_status);

  const patch = async (body) => {
    try { const d = await send(`${base}/items/${it.id}`, 'PATCH', body); onSaved(d.item); } catch (e) { setError(e.message); }
  };
  const save = () => patch({ title, text, ...(level?.is_test ? { steps } : {}) });
  const decide = (decision) => patch({ ...(dirty ? { title, text, ...(level?.is_test ? { steps } : {}) } : {}), decision });
  const showSection = async () => {
    try { setSection(await api(`${base}/files/${src.file_id}/sections/${src.section}`)); } catch (e) { setError(e.message); }
  };
  const startSplit = () => {
    const [a, b] = splitGuess(text);
    setParts([{ title: `${title} (1)`, text: a }, { title: `${title} (2)`, text: b }]);
  };
  const move = async (blockId) => {
    const to = levels.find(l => l.block_id === blockId);
    if (!to || !window.confirm(`Move this item to ${to.label}?\n\nIts Orcanos match and its links are dropped — add links again on the Links step. A moved item is not checked against the ${to.label} items already in Orcanos.`)) return;
    try {
      const d = await send(`${base}/items/${it.id}`, 'PATCH', { block_id: blockId });
      onSaved(d.item);
      if (d.links_dropped) onSplit();      // links changed — reload what depends on them
    } catch (e) { setError(e.message); }
  };
  const doSplit = async () => {
    try { await send(`${base}/items/${it.id}/split`, 'POST', { parts }); setParts(null); onSplit(); } catch (e) { setError(e.message); }
  };

  const Box = big ? 'textarea' : 'input';         // large view: steps wrap instead of being cut off
  const panel = (
    <aside className={`di-side di-item-panel${big ? ' di-item-panel--big' : ''}`} role={big ? 'dialog' : undefined} aria-modal={big || undefined}>
      <div className="di-panel-head">
        <div className="di-label">Selected item</div>
        <button type="button" className="di-link" onClick={() => setBig(b => !b)} title={big ? 'Back to the side panel (Esc)' : 'Open in a larger window to read and edit'}>
          {big ? '✕ Close' : '⤢ Open larger'}
        </button>
      </div>
      <div className="di-level-box">
        <div><span className="di-muted di-small">Level</span> <b>{level?.label}</b></div>
        {it.moved_from
          ? <div className="di-small">Moved here from {it.moved_from}. Not checked against the {level?.label} items in Orcanos.</div>
          : it.level_reason && <div className="di-small">{it.level_reason}</div>}
        {!written && !locked && levels.length > 1 && (
          <SearchSelect ariaLabel="Move to level" placeholder="Move to level…" value="" onChange={v => v && move(v)}
            options={levels.filter(l => l.block_id !== it.block_id).map(l => ({ value: l.block_id, label: l.label }))} />
        )}
      </div>
      <div className="di-pair">
        <label className="di-field">Old ID <span className="di-muted di-small">— from your old system</span>
          <input type="text" value={oldId} disabled={locked || written} placeholder="none"
            onChange={e => setOldId(e.target.value)} onBlur={() => { if (oldId.trim() !== (it.doc_ref || '')) patch({ doc_ref: oldId }); }} />
        </label>
        <label className="di-field">Paragraph <span className="di-muted di-small">— in the document</span>
          <input type="text" list={`di-paras-${it.block_id}`} value={para} disabled={locked || written} placeholder="none"
            onChange={e => setPara(e.target.value)} onBlur={() => { if (para.trim() !== (it.paragraph || '')) patch({ paragraph: para }); }} />
          <datalist id={`di-paras-${it.block_id}`}>{paragraphs.map(p => <option key={p} value={p} />)}</datalist>
        </label>
      </div>
      {Object.keys(it.fields || {}).length > 0 && (
        <div className="di-small"><span className="di-label">Orcanos fields</span>{' '}
          <span className="di-muted">— from the sheet's columns, written at Create</span>
          <div className="di-chips">{Object.entries(it.fields).map(([k, v]) => (
            <span key={k} className="di-chip" title={v}>{k}: {String(v).length > 40 ? `${String(v).slice(0, 40)}…` : v}</span>))}</div>
        </div>
      )}
      <label className="di-field">Title
        <input type="text" value={title} disabled={locked || written} onChange={e => setTitle(e.target.value)} />
      </label>
      <label className="di-field">{level?.is_test ? 'Objective' : 'Requirement'}
        <textarea rows={big ? 8 : 4} value={text} disabled={locked || written} onChange={e => setText(e.target.value)} />
      </label>
      {level?.is_test && (
        <div className="di-field">Steps
          {big && steps.length > 0 && <div className="di-step di-step-head"><span /><span>Step</span><span>Expected result</span><span /></div>}
          {steps.map((s, i) => (
            <div key={i} className="di-step">
              <span className="di-muted">{i + 1}</span>
              <Box aria-label={`Step ${i + 1}`} rows={big ? 3 : undefined} value={s.description} disabled={locked || written}
                onChange={e => setSteps(st => st.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
              <Box aria-label={`Expected result ${i + 1}`} rows={big ? 3 : undefined} value={s.expected || ''} placeholder="Expected" disabled={locked || written}
                onChange={e => setSteps(st => st.map((x, j) => j === i ? { ...x, expected: e.target.value } : x))} />
              {!written && <button type="button" className="di-x" aria-label="Remove step" onClick={() => setSteps(st => st.filter((_, j) => j !== i))}>×</button>}
            </div>
          ))}
          {!written && !locked && <button type="button" className="di-btn di-btn-s" onClick={() => setSteps(st => [...st, { description: '', expected: '' }])}>+ Step</button>}
        </div>
      )}
      {parts && (
        <div className="di-split">
          <div className="di-label">Split into</div>
          {parts.map((p, i) => (
            <div key={i} className="di-split-part">
              <input aria-label={`Part ${i + 1} title`} value={p.title}
                onChange={e => setParts(ps => ps.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
              <textarea aria-label={`Part ${i + 1} text`} rows={2} value={p.text}
                onChange={e => setParts(ps => ps.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
            </div>
          ))}
          <div className="di-actions">
            <button type="button" className="di-btn di-btn-s" onClick={() => setParts(ps => [...ps, { title: `${title} (${ps.length + 1})`, text: '' }])}>+ Part</button>
            <button type="button" className="di-btn di-btn-s di-btn-p" disabled={parts.filter(p => p.title.trim()).length < 2} onClick={doSplit}>Split</button>
            <button type="button" className="di-link" onClick={() => setParts(null)}>Cancel</button>
          </div>
          <div className="di-muted di-small">Each part keeps the same source and parent links.</div>
        </div>
      )}
      <div className="di-label">{it.kind === 'generated' ? 'Written for' : 'From the document'}</div>
      {(it.sources || []).map((s, i) => (
        <div key={i} className="di-quote">
          <div>“{s.quote}”</div>
          <div className="di-muted di-small">{s.file}{s.anchor ? ` · ${s.anchor}` : ''}</div>
        </div>
      ))}
      {src.file_id && src.section && !section && <button type="button" className="di-link" onClick={showSection}>Show the whole section</button>}
      {section && <div className="di-section-text"><div className="di-small di-muted">{section.file} · {section.anchor}</div>{section.text}</div>}
      {!it.quote_ok && it.decision !== 'rejected' && <div className="di-warn">The quote was not found in the document. Check the item against the file before you accept it.</div>}
      {it.kind === 'rewritten' && it.decision !== 'rejected' && <div className="di-warn">Rewritten by AI from informal text. Check the wording and every number.</div>}
      {it.kind === 'generated' && it.decision !== 'rejected' && <div className="di-warn">Written by AI — no document states it. Accept it only if it is right for this product.</div>}
      {it.reject_reason && <div className="di-note">Left out by the AI: {it.reject_reason}</div>}
      {(it.match === 'exists' || it.match === 'dup' || it.match_key) && !written && (
        <div className="di-match">
          <div className="di-label">In Orcanos</div>
          {it.match_key && <div><b>{String(it.match_key).split(' (')[0]}</b> {it.match_name}</div>}
          {it.match_reason && <div className="di-muted di-small">{it.match_reason}</div>}
          {it.match_text && <div className="di-quote di-small"><div className="di-muted">In Orcanos now:</div>{it.match_text}</div>}
          {it.match_key && (
            <div className="di-seg">
              <button type="button" className={it.match === 'exists' ? 'on' : ''} disabled={locked} onClick={() => patch({ match: 'exists' })}>Link to it — don't create</button>
              <button type="button" className={it.match === 'update' ? 'on' : ''} disabled={locked} onClick={() => patch({ match: 'update' })}>Update it in Orcanos</button>
              <button type="button" className={!['exists', 'update'].includes(it.match) ? 'on' : ''} disabled={locked} onClick={() => patch({ match: 'new' })}>Create as new</button>
            </div>
          )}
          {it.match === 'update' && <div className="di-warn">Create will overwrite this item's name and description in Orcanos with the text above. If the item is approved, check your workflow allows that first.</div>}
        </div>
      )}
      {it.create_error && <div className="di-error">{it.create_error}</div>}
      {!written && !locked && (
        <div className="di-actions">
          <button type="button" className="di-btn di-btn-p" onClick={() => decide('accepted')}>Accept</button>
          <button type="button" className="di-btn" onClick={() => decide('rejected')}>Reject</button>
          {dirty && <button type="button" className="di-btn" onClick={save}>Save changes</button>}
          {!parts && <button type="button" className="di-btn" onClick={startSplit}>Split</button>}
          {it.decision !== 'pending' && <button type="button" className="di-link" onClick={() => decide('pending')}>Undo decision</button>}
        </div>
      )}
      {written && it.created_url && <a href={it.created_url} target="_blank" rel="noreferrer">Open in Orcanos ↗</a>}
    </aside>
  );
  return big
    ? <div className="di-pop-back" onMouseDown={e => { if (e.target === e.currentTarget) setBig(false); }}>{panel}</div>
    : panel;
}

function ItemsStep({ data, base, setError, items, setItems, reloadItems }) {
  const { levels, run } = data;
  const locked = BUSY.includes(run.status);
  const [tab, setTab] = useState(levels[0]?.block_id);
  const [filter, setFilter] = useState('all');
  const [sel, setSel] = useState(new Set());
  const [cur, setCur] = useState(null);

  const inTab = items.filter(i => i.block_id === tab);
  const tabLabel = levels.find(l => l.block_id === tab)?.label || 'this level';
  const shown = inTab.filter(i => matchesFilter(i, filter));
  const current = items.find(i => i.id === cur) || null;
  useEffect(() => { setSel(new Set()); }, [tab, filter]);

  const count = (bid, f) => items.filter(i => i.block_id === bid && matchesFilter(i, f)).length;
  const toggle = id => setSel(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const decide = async (decision) => {
    let ids = Array.from(sel);
    const risky = items.filter(i => sel.has(i.id) && (AI_KINDS.includes(i.kind) || !i.quote_ok));
    if (decision === 'accepted' && risky.length &&
        !window.confirm(`${risky.length} of the selected items were written or rewritten by AI, or have a quote that was not found. Accept them too?\n\nOK = accept all selected · Cancel = accept only the others`)) {
      const skip = new Set(risky.map(i => i.id));
      ids = ids.filter(id => !skip.has(id));
    }
    if (!ids.length) return;
    try {
      await send(`${base}/items/decide`, 'POST', { ids, decision });
      setItems(list => list.map(i => (ids.includes(i.id) && !DONE.includes(i.create_status) ? { ...i, decision } : i)));
      setSel(new Set());
    } catch (e) { setError(e.message); }
  };
  const onSaved = (it) => setItems(list => list.map(i => (i.id === it.id ? it : i)));
  const allSel = shown.length > 0 && shown.every(i => sel.has(i.id));
  const paragraphs = useMemo(() => Array.from(new Set(inTab.map(i => i.paragraph).filter(Boolean))).sort(), [inTab]);
  const [grouping, setGrouping] = useState(false);
  const suggest = async () => {
    if (inTab.some(i => i.paragraph) && !window.confirm(`The AI regroups the ${tabLabel} items into paragraphs — the paragraphs they have now are replaced (they are used as hints). Continue?`)) return;
    setGrouping(true);
    try {
      const d = await send(`${base}/items/paragraphs/suggest`, 'POST', { block_id: tab });
      if (d.error) setError(`Part of the items were not grouped: ${d.error}`);
      await reloadItems();
    } catch (e) { setError(e.message); }
    setGrouping(false);
  };
  const exportList = () => {
    const label = Object.fromEntries(levels.map(l => [l.block_id, l.label]));
    downloadCsv(`${run.name || 'import'} - items.csv`, [
      ['Level', 'Old ID', 'Paragraph', 'Title', 'Text', 'Decision', 'In Orcanos', 'Source'],
      ...items.map(i => [label[i.block_id], i.doc_ref, i.paragraph, i.title, i.text, i.decision,
        i.created_key || (i.match === 'exists' ? String(i.match_key || '').split(' (')[0] : ''),
        (i.sources || []).map(s => `${s.file}${s.anchor ? ` · ${s.anchor}` : ''}`).join('; ')])]);
  };

  return (
    <>
      <div className="di-tabs" role="tablist">
        {levels.map(lv => (
          <button key={lv.block_id} type="button" role="tab" aria-selected={tab === lv.block_id}
            className={tab === lv.block_id ? 'on' : ''} onClick={() => { setTab(lv.block_id); setCur(null); }}>
            {lv.label} · {count(lv.block_id, 'all')}
          </button>
        ))}
      </div>
      <div className="di-toolbar">
        <div className="di-chips">
          {ITEM_FILTERS.map(([k, t]) => (
            <button key={k} type="button" className={`di-fchip ${filter === k ? 'on' : ''}`} onClick={() => setFilter(k)}>
              {t} {count(tab, k)}</button>
          ))}
        </div>
        {!locked && (
          <div className="di-actions">
            <button type="button" className="di-btn" disabled={grouping || inTab.length < 2} onClick={suggest}
              title="One AI call groups this level's items into paragraphs (sub-sections) of the document — e.g. per module. Every paragraph stays editable.">
              {grouping ? 'Grouping…' : '✨ Suggest paragraphs'}</button>
            <button type="button" className="di-btn" onClick={exportList} title="Every item with its old ID, paragraph and decision, as a CSV for Excel">Download the list</button>
            <button type="button" className="di-btn" disabled={!sel.size} onClick={() => decide('rejected')}>Reject selected</button>
            <button type="button" className="di-btn di-btn-p" disabled={!sel.size} onClick={() => decide('accepted')}>Accept selected ({sel.size})</button>
          </div>
        )}
      </div>
      <div className="di-two">
        <div className="di-main di-card di-card-flush">
          <div className="di-scroll-x">
            <div className="di-row di-row-head">
              <input type="checkbox" aria-label="Select all shown" checked={allSel}
                onChange={() => setSel(allSel ? new Set() : new Set(shown.map(i => i.id)))} />
              <span>Proposed item · source</span><span>Orcanos</span><span>Decision</span>
            </div>
            {shown.length === 0 && <div className="di-empty">Nothing here.</div>}
            {shown.map(it => {
              const src = (it.sources || [])[0] || {};
              return (
                <div key={it.id} className={`di-row ${cur === it.id ? 'sel' : ''} ${it.decision === 'rejected' ? 'rej' : ''}`}
                  onClick={() => setCur(it.id)}>
                  <input type="checkbox" aria-label="Select item" checked={sel.has(it.id)}
                    onClick={e => e.stopPropagation()} onChange={() => toggle(it.id)} />
                  <div>
                    <div className="di-row-title">{it.title}</div>
                    <div className="di-row-text">{it.text}</div>
                    {(it.moved_from || it.level_reason) && (
                      <div className="di-row-why">{it.moved_from ? `Moved here from ${it.moved_from}` : `Why ${tabLabel}: ${it.level_reason}`}</div>
                    )}
                    <div className="di-row-meta">
                      {it.doc_ref && <span className="di-chip" title="ID in your old system">{it.doc_ref}</span>}
                      {it.paragraph && <span className="di-chip di-chip-level" title="Paragraph in the document">¶ {it.paragraph}</span>}
                      <KindChip kind={it.kind} />
                      {!it.quote_ok && <span className="di-chip di-chip-red">Quote not found</span>}
                      <span className="di-muted di-small">{src.file}{src.anchor ? ` · ${src.anchor}` : ''}{it.sources?.length > 1 ? ` · +${it.sources.length - 1} more` : ''}</span>
                    </div>
                  </div>
                  <div><MatchChip it={it} /></div>
                  <div><DecisionChip d={it.decision} /></div>
                </div>
              );
            })}
          </div>
        </div>
        {current ? (
          <ItemPanel it={current} level={levels.find(l => l.block_id === current.block_id)} levels={levels} base={base}
            onSaved={onSaved} onSplit={reloadItems} setError={setError} locked={locked} paragraphs={paragraphs} />
        ) : (
          <aside className="di-side"><div className="di-muted">Click an item to see its quote, edit it, and accept or reject it.</div></aside>
        )}
      </div>
    </>
  );
}

// ------------------------------------------------------------------ step 3: links
const BASIS = {
  named: ['Named in the document', 'di-chip-ok'],
  table: ['From a trace table', 'di-chip-ok'],
  old_id: ['From old IDs', 'di-chip-ok'],
  ai: ['Suggested by AI', 'di-chip-amber'],
  manual: ['Added by you', 'di-chip-purple'],
};

// Field picker options: "Not mapped" first, then the form's fields A→Z.
const TEXT_FIELD_TYPES = ['text', 'textArea', 'textarea', 'richEditor', 'html', 'memo'];
const fieldChoices = (list) => [{ value: '', label: 'Not mapped' },
  ...list.map(f => ({ value: f.title, label: f.title })).sort((a, b) => a.label.localeCompare(b.label))];

function LinkPicker({ options, onPick, onCancel, allowKey }) {
  const [v, setV] = useState('');
  const [key, setKey] = useState('');
  const choices = useMemo(() => options.map(o => ({ value: o.id, label: o.title || '' }))
    .sort((a, b) => a.label.localeCompare(b.label)), [options]);
  return (
    <div className="di-picker">
      <SearchSelect ariaLabel="Choose an item" placeholder="Search items…" value={v} onChange={x => setV(String(x || ''))} options={choices} />
      {allowKey && <input aria-label="Or an Orcanos key" placeholder="or Orcanos key, e.g. UR-123" value={key} onChange={e => setKey(e.target.value)} />}
      <button type="button" className="di-btn di-btn-s" disabled={!v && !key.trim()} onClick={() => onPick(v, key.trim())}>Link</button>
      <button type="button" className="di-link" onClick={onCancel}>Cancel</button>
    </div>
  );
}

// Write with AI for every parent with no child on a level (POST items/write-gaps, a background job).
async function writeGaps(base, lv, n, parentLabel, acceptedOnly, reload, setError) {
  if (!window.confirm(`The AI will write about 3 ${lv.label} items for each of ${n} ${parentLabel} (at most 40) — one AI call each. They come back as Written by AI, waiting for your decision. Continue?`)) return;
  try { await send(`${base}/items/write-gaps`, 'POST', { block_id: lv.block_id, accepted_only: acceptedOnly }); reload(); } catch (e) { setError(e.message); }
}

function LinksStep({ data, base, setError, items, links, setLinks, reloadLinks, reloadAll, reload }) {
  const { levels, run } = data;
  const locked = BUSY.includes(run.status);
  const [writing, setWriting] = useState(null);     // `${parentId}:${blockId}` while the AI writes
  const [filter, setFilter] = useState('all');
  const [picking, setPicking] = useState(null);     // {kind:'parent', child} | {kind:'child', parent, block}
  const live = items.filter(i => i.decision !== 'rejected');
  const byId = useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items]);
  const lvl = useMemo(() => Object.fromEntries(levels.map(l => [l.block_id, l])), [levels]);
  const childLevels = bid => levels.filter(l => l.parent_block_id === bid);
  const isExisting = ln => !!ln.parent_key || !!ln.child_key || byId[ln.child_item]?.match === 'exists' || byId[ln.parent_item]?.match === 'exists';
  const visible = ln => ln.decision !== 'removed' || filter === 'removed';
  const fits = ln => filter === 'all' || filter === 'removed' ? true
    : filter === 'existing' ? isExisting(ln) : ln.basis === filter;

  const setDecision = async (ids, decision) => {
    try {
      await send(`${base}/links/decide`, 'POST', { ids, decision });
      setLinks(ls => ls.map(l => (ids.includes(l.id) ? { ...l, decision } : l)));
    } catch (e) { setError(e.message); }
  };
  const [relinking, setRelinking] = useState(false);
  const relink = async () => {
    setRelinking(true);
    try {
      const r = await send(`${base}/relink`, 'POST');
      await reloadAll();
      window.alert(r.links_added
        ? `${r.links_added} new link(s) found (${r.to_existing} to items already in Orcanos). Keep them, then Create writes only the new ones.`
        : 'No new links — every id the trace columns name is already linked, or names nothing in this import.');
    } catch (e) { setError(e.message); }
    setRelinking(false);
  };
  const writeWithAi = async (parentId, blockId) => {
    setWriting(`${parentId}:${blockId}`);
    try { await send(`${base}/items/write`, 'POST', { block_id: blockId, parent_item: parentId }); await reloadAll(); } catch (e) { setError(e.message); }
    setWriting(null);
  };
  // No parent link: the AI picks a parent from the level above, else writes one — linked either way.
  const parentWithAi = async (childId) => {
    setWriting(`p:${childId}`);
    try { await send(`${base}/items/parent-ai`, 'POST', { child_item: childId }); await reloadAll(); } catch (e) { setError(e.message); }
    setWriting(null);
  };
  const parentWithAiAll = async (rows) => {
    if (!window.confirm(`The AI will link or write a parent for ${rows.length} item(s) — one AI call each. Continue?`)) return;
    for (const r of rows) {
      setWriting(`p:${r.id}`);
      try { await send(`${base}/items/parent-ai`, 'POST', { child_item: r.id }); } catch (e) { setError(e.message); break; }
    }
    setWriting(null);
    await reloadAll();
  };
  const addLink = async (childId, parentItem, parentKey) => {
    try {
      await send(`${base}/links`, 'POST', { child_item: childId, parent_item: parentItem || '', parent_key: parentKey || '', parent_name: parentKey || '' });
      setPicking(null);
      reloadLinks();
    } catch (e) { setError(e.message); }
  };

  const linksOfParent = (pid) => links.filter(l => l.parent_item === pid && visible(l));
  const missingParent = live.filter(i => lvl[i.block_id]?.parent_block_id
    && !links.some(l => l.child_item === i.id && l.decision !== 'removed'));

  // Plain render functions, not components: a component defined in here would be a NEW type on
  // every render (App re-renders every second, Note #59) and remount — losing an open picker.
  const renderLinkRow = (ln, depth) => {
    // The child may already be in Orcanos (an old ID an earlier import saved) — then only its key.
    const child = byId[ln.child_item] || (ln.child_key ? {
      id: '', title: ln.child_name || ln.child_key, block_id: ln.child_block, match: 'exists', match_key: ln.child_key, decision: 'accepted',
    } : null);
    if (!child) return null;
    const [label, cls] = BASIS[ln.basis] || BASIS.ai;
    return (
      <React.Fragment key={ln.id}>
        <div className={`di-node ${ln.decision === 'removed' ? 'rej' : ''}`} style={{ paddingLeft: 14 + depth * 28 }}>
          <span className="di-lvl">L{lvl[child.block_id]?.depth}</span>
          <span className="di-node-key">{child.match === 'exists' ? String(child.match_key).split(' (')[0] : 'new'}</span>
          <span className="di-node-title">{child.title}{child.decision === 'pending' && <span className="di-muted di-small"> · not decided</span>}</span>
          <span className={`di-chip ${cls}`}>{label}</span>
          {isExisting(ln) && <span className="di-chip di-chip-purple">Existing item</span>}
          <span className="di-muted di-small di-node-why">{ln.reason}</span>
          {!locked && ln.write_status !== 'written' && (
            <span className="di-node-actions">
              <button type="button" className={`di-btn di-btn-s ${ln.decision === 'kept' ? 'on' : ''}`} onClick={() => setDecision([ln.id], 'kept')}>Keep</button>
              <button type="button" className={`di-btn di-btn-s ${ln.decision === 'removed' ? 'on' : ''}`} onClick={() => setDecision([ln.id], ln.decision === 'removed' ? 'pending' : 'removed')}>
                {ln.decision === 'removed' ? 'Restore' : 'Remove'}</button>
            </span>
          )}
          {ln.write_status === 'written' && <span className="di-chip di-chip-ok">Written</span>}
        </div>
        {depth < 6 && child.id && renderSubtree(child.id, child.block_id, depth + 1)}
      </React.Fragment>
    );
  };

  const renderSubtree = (parentId, blockId, depth) => {
    const rows = linksOfParent(parentId).filter(fits);
    const parent = byId[parentId];
    const gapLevels = childLevels(blockId).filter(cl => cl.must_have
      && !links.some(l => l.parent_item === parentId && l.child_block === cl.block_id && l.decision !== 'removed'));
    return (
      <React.Fragment key={`sub-${parentId}`}>
        {rows.map(ln => renderLinkRow(ln, depth))}
        {filter === 'all' && parent && parent.decision !== 'rejected' && gapLevels.map(cl => {
          const open = picking?.kind === 'child' && picking.parent === parentId && picking.block === cl.block_id;
          return (
            <div key={cl.block_id} className="di-node di-gap" style={{ paddingLeft: 14 + depth * 28 }}>
              <span className="di-lvl">L{cl.depth}</span>
              <span className="di-node-title">No {cl.label} found in the documents</span>
              {!locked && (open ? (
                <LinkPicker options={live.filter(i => i.block_id === cl.block_id)}
                  onPick={(v) => addLink(v, parentId, '')} onCancel={() => setPicking(null)} />
              ) : (
                <>
                  <button type="button" className="di-btn di-btn-s" onClick={() => setPicking({ kind: 'child', parent: parentId, block: cl.block_id })}>Link one…</button>
                  <button type="button" className="di-btn di-btn-s" disabled={!!writing} onClick={() => writeWithAi(parentId, cl.block_id)}
                    title="The AI drafts items for this one; they are marked Written by AI and wait for your decision">
                    {writing === `${parentId}:${cl.block_id}` ? 'Writing…' : '✨ Write with AI'}</button>
                </>
              ))}
            </div>
          );
        })}
      </React.Fragment>
    );
  };

  const roots = live.filter(i => !lvl[i.block_id]?.parent_block_id);
  const existingParents = Array.from(new Map(links.filter(l => l.parent_key && visible(l) && fits(l))
    .map(l => [l.parent_key, l])).values());
  const pending = links.filter(l => l.decision === 'pending');
  const named = links.filter(l => ['named', 'table', 'old_id'].includes(l.basis) && l.decision === 'pending');
  const unmatched = run.result?.trace_unmatched || [];
  const unmatchedTotal = run.result?.trace_unmatched_total || unmatched.length;
  // Parents (this run, not rejected) with no un-removed link to a child of the level — the server's gap_parents.
  const gaps = levels.filter(cl => lvl[cl.parent_block_id]).map(cl => ({
    lv: cl, parent: lvl[cl.parent_block_id],
    n: live.filter(p => p.block_id === cl.parent_block_id
      && !links.some(l => l.parent_item === p.id && l.child_block === cl.block_id && l.decision !== 'removed')).length,
  })).filter(g => g.n > 0);

  // The panel's trace chart, as in panel edit — so "L3" on a row reads as a type.
  const chart = levels.map(l => ({
    id: l.block_id, parent: l.parent_block_id || null, depth: l.depth, isRoot: !l.parent_block_id,
    optional: !l.must_have, label: `L${l.depth} ${l.label}`, title: l.meaning || l.label,
  }));

  return (
    <div className="di-three">
      <aside className="di-side di-chart">
        <div className="di-card-title">Trace chart</div>
        <BlockTreePreview nodes={chart} />
        <div className="di-muted di-small">The panel's levels. Dashed = optional.</div>
      </aside>
      <div className="di-main">
        <div className="di-toolbar">
          <div className="di-chips">
            {[['all', 'All links'], ['table', 'From a trace table'], ['old_id', 'From old IDs'], ['named', 'Named in the document'], ['ai', 'Suggested by AI'], ['existing', 'Existing item'], ['removed', 'Removed']].map(([k, t]) => (
              <button key={k} type="button" className={`di-fchip ${filter === k ? 'on' : ''}`} onClick={() => setFilter(k)}>
                {t} {k === 'all' ? links.filter(l => l.decision !== 'removed').length
                  : k === 'removed' ? links.filter(l => l.decision === 'removed').length
                    : k === 'existing' ? links.filter(isExisting).length : links.filter(l => l.basis === k).length}
              </button>
            ))}
          </div>
        </div>
        {unmatchedTotal > 0 && (
          <details className="di-warn">
            <summary>{unmatchedTotal} trace-table {unmatchedTotal === 1 ? 'row was' : 'rows were'} not turned into a link — open to see why</summary>
            <ul>{unmatched.map((u, i) => <li key={i}><b>{u.parent_ref} → {u.child_ref}</b> · {u.why} <span className="di-muted">({u.where})</span></li>)}</ul>
            {unmatchedTotal > unmatched.length && <div className="di-small">Showing the first {unmatched.length}.</div>}
          </details>
        )}
        <div className="di-card di-card-flush">
          {roots.map(r => (
            <React.Fragment key={r.id}>
              <div className="di-node di-node-root">
                <span className="di-lvl">L{lvl[r.block_id]?.depth}</span>
                <span className="di-node-key">{r.match === 'exists' ? String(r.match_key).split(' (')[0] : 'new'}</span>
                <span className="di-node-title"><b>{r.title}</b>{r.decision === 'pending' && <span className="di-muted di-small"> · not decided</span>}</span>
              </div>
              {renderSubtree(r.id, r.block_id, 1)}
            </React.Fragment>
          ))}
          {existingParents.map(p => (
            <React.Fragment key={p.parent_key}>
              <div className="di-node di-node-root">
                <span className="di-node-key">{p.parent_key}</span>
                <span className="di-node-title"><b>{p.parent_name || p.parent_key}</b> <span className="di-chip di-chip-purple">Existing item</span></span>
              </div>
              {links.filter(l => l.parent_key === p.parent_key && visible(l) && fits(l)).map(ln => renderLinkRow(ln, 1))}
            </React.Fragment>
          ))}
          {filter === 'all' && missingParent.length > 0 && (
            <>
              <div className="di-node di-node-root di-gap"><b>No parent link</b><span className="di-muted di-small di-node-title">These would be created without a trace.</span>
                {!locked && <button type="button" className="di-btn di-btn-s" disabled={!!writing} onClick={() => parentWithAiAll(missingParent)}
                  title="For each one the AI picks a parent on the level above, or writes one">✨ Link or write all with AI ({missingParent.length})</button>}
              </div>
              {missingParent.map(i => {
                const pl = lvl[lvl[i.block_id].parent_block_id];
                const open = picking?.kind === 'parent' && picking.child === i.id;
                return (
                  <div key={i.id} className="di-node" style={{ paddingLeft: 42 }}>
                    <span className="di-lvl">L{lvl[i.block_id]?.depth}</span>
                    <span className="di-node-title">{i.title}</span>
                    {!locked && (open ? (
                      <LinkPicker allowKey options={live.filter(x => x.block_id === pl.block_id)}
                        onPick={(v, key) => addLink(i.id, v, key)} onCancel={() => setPicking(null)} />
                    ) : (
                      <>
                        <button type="button" className="di-btn di-btn-s" onClick={() => setPicking({ kind: 'parent', child: i.id })}>Link a {pl.label}…</button>
                        <button type="button" className="di-btn di-btn-s" disabled={!!writing} onClick={() => parentWithAi(i.id)}
                          title={`The AI links the best ${pl.label} of this import, or writes a new one (Written by AI, waiting for your decision)`}>
                          {writing === `p:${i.id}` ? 'Working…' : '✨ Link or write with AI'}</button>
                      </>
                    ))}
                  </div>
                );
              })}
            </>
          )}
          {!roots.length && !existingParents.length && <div className="di-empty">No items to link.</div>}
        </div>
      </div>
      <aside className="di-side">
        <div className="di-card-title">How links are found</div>
        <div><span className="di-chip di-chip-ok">From a trace table</span>
          <div className="di-muted di-small">A row of a table you marked as a trace table on the Documents step. Matched by the ids — no AI.</div></div>
        <div><span className="di-chip di-chip-ok">From old IDs</span>
          <div className="di-muted di-small">An earlier import saved this ID from your old system with its Orcanos key, and a trace column names it. Matched by the ID — no AI.</div></div>
        <div><span className="di-chip di-chip-ok">Named in the document</span>
          <div className="di-muted di-small">The document cites the other item by its number or title. The strongest evidence.</div></div>
        <div><span className="di-chip di-chip-amber">Suggested by AI</span>
          <div className="di-muted di-small">Same subject, limit or behaviour. Each says why — check these.</div></div>
        <div><span className="di-chip di-chip-purple">Existing item</span>
          <div className="di-muted di-small">One end is already in Orcanos — only the trace is added.</div></div>
        <div className="di-note">Links follow the panel's levels. Only kept links are written; a link to a rejected item is skipped.</div>
        {!locked && (
          <>
            <button type="button" className="di-btn" disabled={!named.length} onClick={() => setDecision(named.map(l => l.id), 'kept')}>Keep all named, table and old-ID links ({named.length})</button>
            <button type="button" className="di-btn" disabled={!pending.length} onClick={() => setDecision(pending.map(l => l.id), 'kept')}>Keep every proposed link ({pending.length})</button>
            <button type="button" className="di-btn" disabled={relinking} onClick={relink}
              title="Reads the trace columns of your Excel sheets again (every column marked Trace IDs) and adds the links they name that are missing here. Items already in Orcanos stay as they are — keep the new links, then Create writes only them.">
              {relinking ? 'Reading…' : 'Re-read trace columns'}</button>
          </>
        )}
        {!locked && gaps.length > 0 && (
          <>
            <div className="di-card-title">Fill the gaps with AI</div>
            {gaps.map(g => (
              <div key={g.lv.block_id} className="di-gapfill">
                <div className="di-small"><b>{g.n} {g.parent.label}</b> <span className="di-muted">without a</span> <b>{g.lv.label}</b></div>
                <button type="button" className="di-btn di-btn-s" disabled={!!writing}
                  onClick={() => writeGaps(base, g.lv, g.n, g.parent.label, false, reload, setError)}>✨ Write</button>
              </div>
            ))}
            <div className="di-muted di-small">At most 40 at a time. Each draft is linked to its parent and waits for your decision.</div>
          </>
        )}
      </aside>
    </div>
  );
}

// ------------------------------------------------------------------ step 4: create
function FieldMap({ lv, base, mapping, onSave, setError, locked }) {
  const [fields, setFields] = useState(null);
  const [m, setM] = useState(mapping || {});
  const [open, setOpen] = useState(false);
  const load = async () => {
    setOpen(true);
    if (fields) return;
    try { setFields((await api(`${base}/fields?block_id=${encodeURIComponent(lv.block_id)}`)).fields); } catch (e) { setError(e.message); setOpen(false); }
  };
  const textFields = (fields || []).filter(f => !f.options?.length || TEXT_FIELD_TYPES.includes(f.type));
  const origin = (fields || []).find(f => f.title === m.origin);
  const originIsList = origin && origin.options?.length && !TEXT_FIELD_TYPES.includes(origin.type);
  const summary = [mapping?.source && `Source → ${mapping.source}`, mapping?.origin && `AI mark → ${mapping.origin}`].filter(Boolean).join(' · ');
  return (
    <div className="di-fieldmap">
      <div className="di-card-row">
        <span><b>{lv.label}</b> <span className="di-muted di-small">{summary || 'no fields mapped'}</span></span>
        {!open && !locked && <button type="button" className="di-btn di-btn-s" onClick={load}>Map fields</button>}
      </div>
      {open && (fields === null ? <div className="di-muted di-small">Reading the form from Orcanos…</div> : (
        <div className="di-fieldmap-grid">
          <label className="di-field">Source field — where the item came from
            <SearchSelect ariaLabel="Source field" placeholder="Search fields…" value={m.source || ''}
              onChange={v => setM({ ...m, source: v || '' })} options={fieldChoices(textFields)} />
          </label>
          <label className="di-field">AI mark field — set on items the AI wrote or rewrote
            <SearchSelect ariaLabel="AI mark field" placeholder="Search fields…" value={m.origin || ''}
              onChange={v => setM({ ...m, origin: v || '', origin_value: '' })} options={fieldChoices(fields)} />
          </label>
          {m.origin && (originIsList ? (
            <label className="di-field">Value
              <SearchSelect ariaLabel="AI mark value" placeholder="Search values…" value={m.origin_value || ''}
                onChange={v => setM({ ...m, origin_value: v || '' })}
                options={origin.options.map(o => ({ value: o, label: o }))} />
            </label>
          ) : (
            <label className="di-field">Value
              <input value={m.origin_value || ''} placeholder="Rewritten by AI / Written by AI" onChange={e => setM({ ...m, origin_value: e.target.value })} />
            </label>
          ))}
          <div className="di-actions">
            <button type="button" className="di-btn di-btn-s di-btn-p" onClick={async () => { await onSave(lv.block_id, m); setOpen(false); }}>Save</button>
            <button type="button" className="di-link" onClick={() => { setM(mapping || {}); setOpen(false); }}>Cancel</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// A field the import did not set (e.g. Category) — set afterwards on the items this run created or
// updated: one value for all, or ✨ one per item; checked in the table, then written in the background.
function SetFieldAfter({ levels, items, base, after, reload, setError }) {
  const made = items.filter(i => ['created', 'linked', 'updated'].includes(i.create_status) && (i.created_id || i.created_key));
  const lvs = levels.filter(lv => made.some(i => i.block_id === lv.block_id));
  const [blockId, setBlockId] = useState('');
  const [fields, setFields] = useState(null);
  const [field, setField] = useState('');
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [cols, setCols] = useState([]);
  const [missing, setMissing] = useState([]);
  const bid = blockId || lvs[0]?.block_id || '';
  const rows = made.filter(i => i.block_id === bid);
  const f = (fields || []).find(x => x.title === field);
  const isList = !!(f && f.options?.length && !TEXT_FIELD_TYPES.includes(f.type));

  useEffect(() => {
    if (!bid) return;
    let gone = false;
    setFields(null); setField(''); setVals({}); setCols([]); setMissing([]);
    api(`${base}/fields?block_id=${encodeURIComponent(bid)}`)
      .then(d => { if (!gone) setFields(d.fields); })
      .catch(e => { if (!gone) { setFields([]); setError(e.message); } });
    api(`${base}/after/columns?block_id=${encodeURIComponent(bid)}`)
      .then(d => { if (!gone) setCols(d.columns); })
      .catch(() => {});
    return () => { gone = true; };
  }, [bid, base, setError]);
  const pickField = t => {
    setField(t); setNote(''); setMissing([]);
    setVals(Object.fromEntries(rows.map(i => [i.id, (i.fields || {})[t] || ''])));
  };
  if (!lvs.length) return null;

  const suggest = async () => {
    setBusy('ai'); setNote('');
    try {
      const d = await send(`${base}/after/suggest`, 'POST', { block_id: bid, field });
      setVals(v => ({ ...v, ...d.values }));
      const n = Object.keys(d.values).length;
      setNote(`The AI chose a value for ${n} of ${d.asked} items${n < d.asked ? ' — the rest fit none of the values' : ''}. Check them, then write.`
        + (d.error ? ` Stopped early: ${d.error}` : ''));
    } catch (e) { setError(e.message); }
    setBusy('');
  };
  const fromColumn = async key => {
    const c = cols.find(x => `${x.file_id}|${x.table_id}|${x.col}` === key);
    if (!c) return;
    setBusy('col'); setNote(''); setMissing([]);
    try {
      const d = await send(`${base}/after/from-column`, 'POST', { block_id: bid, field, file_id: c.file_id, table_id: c.table_id, col: c.col });
      setVals(v => ({ ...v, ...d.values }));
      setMissing(d.missing);
      const n = Object.keys(d.values).length;
      setNote(`Took a value from the file for ${n} of ${d.items} items.`
        + (d.no_row ? ` ${d.no_row} items were not found in that sheet.` : '')
        + (d.missing.length ? ' Some values are not in the Orcanos list — see below.' : ''));
    } catch (e) { setError(e.message); }
    setBusy('');
  };
  const write = async () => {
    setBusy('write');
    try { await send(`${base}/after/write`, 'POST', { block_id: bid, field, values: delta }); reload(); } catch (e) { setError(e.message); }
    setBusy('');
  };
  // Only the delta goes: a row whose value is what this import already wrote there is skipped.
  const delta = Object.fromEntries(rows
    .filter(i => (vals[i.id] || '').trim() && (vals[i.id] || '').trim() !== ((i.fields || {})[field] || '').trim())
    .map(i => [i.id, vals[i.id]]));
  const filled = Object.keys(delta).length;
  const withOldId = rows.filter(i => (i.doc_ref || '').trim()).length;
  const choices = isList ? [{ value: '', label: '— leave —' }, ...f.options.map(o => ({ value: o, label: o }))] : null;

  return (
    <div className="di-card">
      <div className="di-card-title">Set a field on the created items</div>
      <div className="di-muted di-small">For a field the import did not fill — e.g. a requirement's Category. Only items this import created or updated are changed.</div>
      <div className="di-fieldmap-grid">
        {lvs.length > 1 && (
          <label className="di-field">Level
            <SearchSelect ariaLabel="Level" value={bid} onChange={v => setBlockId(v || '')}
              options={lvs.map(lv => ({ value: lv.block_id, label: `${lv.label} (${made.filter(i => i.block_id === lv.block_id).length})` }))} />
          </label>
        )}
        <label className="di-field">Field
          {fields === null ? <span className="di-muted di-small">Reading the form from Orcanos…</span> : (
            <SearchSelect ariaLabel="Field" placeholder="Search fields…" value={field} onChange={v => pickField(v || '')}
              options={fieldChoices(fields).slice(1)} />
          )}
        </label>
        {f && (
          <label className="di-field">Same value for all
            {isList ? (
              <SearchSelect ariaLabel="Value for all" placeholder="Search values…" value=""
                onChange={v => v && setVals(Object.fromEntries(rows.map(i => [i.id, v])))} options={choices.slice(1)} />
            ) : (
              <input placeholder="Type, then press Enter" onKeyDown={e => {
                if (e.key === 'Enter') setVals(Object.fromEntries(rows.map(i => [i.id, e.target.value])));
              }} />
            )}
          </label>
        )}
        {f && cols.length > 0 && (
          <label className="di-field">From a file column
            <SearchSelect ariaLabel="From a file column" placeholder="Search columns…" value=""
              onChange={v => v && fromColumn(v)}
              options={cols.map(c => ({ value: `${c.file_id}|${c.table_id}|${c.col}`, label: c.label }))} />
          </label>
        )}
      </div>
      {f && (
        <>
          <div className="di-card-row">
            {isList && <button type="button" className="di-btn di-btn-s" disabled={!!busy} onClick={suggest}
              title="One AI call per 150 items picks the value that fits each item, from the field's list only">
              {busy === 'ai' ? 'Choosing…' : '✨ Suggest per item'}</button>}
            {!isList && withOldId > 0 && <button type="button" className="di-btn di-btn-s" disabled={!!busy}
              title="Each item's ID in your old system, as kept by this import"
              onClick={() => setVals(v => ({ ...v, ...Object.fromEntries(rows.filter(i => (i.doc_ref || '').trim()).map(i => [i.id, i.doc_ref.trim()])) }))}>
              Fill from old ID ({withOldId})</button>}
            <span className="di-muted di-small">{filled} of {rows.length} items to write (new or changed)</span>
          </div>
          {busy === 'col' && <div className="di-muted di-small">Reading the file…</div>}
          {note && <div className="di-muted di-small">{note}</div>}
          {missing.length > 0 && (
            <div className="di-warn">Not in the Orcanos list of {field} — left empty. Add them to the list in Orcanos, then pick the column again:
              <div className="di-chips">{missing.slice(0, 40).map(m => <span key={m.value} className="di-chip di-chip-amber">{m.value} · {m.count}</span>)}</div>
            </div>
          )}
          <div className="di-scroll-x di-after-table">
            <table className="di-table">
              <thead><tr><th>Item</th><th>{field}</th></tr></thead>
              <tbody>
                {rows.map(i => (
                  <tr key={i.id}>
                    <td><b>{i.created_key}</b> {i.title}</td>
                    <td>{isList ? (
                      <select value={vals[i.id] || ''} onChange={e => setVals(v => ({ ...v, [i.id]: e.target.value }))}>
                        {choices.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input value={vals[i.id] || ''} onChange={e => setVals(v => ({ ...v, [i.id]: e.target.value }))} />
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="di-btn di-btn-p" disabled={!!busy || !filled} onClick={write}>
            {busy === 'write' ? 'Starting…' : `Write ${field} to ${filled} items in Orcanos`}</button>
        </>
      )}
      {after && (
        <div className="di-chips">
          <span className="di-chip di-chip-ok">{after.field} set on {after.written}</span>
          {after.failed > 0 && <span className="di-chip di-chip-red">{after.failed} failed</span>}
        </div>
      )}
      {after?.errors?.length > 0 && <ul className="di-fails">{after.errors.map((e, n) => <li key={n}>{e}</li>)}</ul>}
    </div>
  );
}

function GapSummary({ levels, items, links, accepted, noParent, base, reload, setError, setStep, locked }) {
  const lvl = Object.fromEntries(levels.map(l => [l.block_id, l]));
  const ok = new Set(accepted.map(i => i.id));
  // Accepted parents with no KEPT link to an ACCEPTED child of the level — only those are written
  // (the server's gap_parents with strict=True).
  const gaps = levels.filter(cl => lvl[cl.parent_block_id]).map(cl => ({
    lv: cl, parent: lvl[cl.parent_block_id],
    rows: accepted.filter(p => p.block_id === cl.parent_block_id
      && !links.some(l => l.parent_item === p.id && l.child_block === cl.block_id && l.decision === 'kept' && (ok.has(l.child_item) || (!l.child_item && l.child_key)))),
  })).filter(g => g.rows.length);
  const undecided = items.filter(i => i.decision === 'pending').length;
  const openLinks = links.filter(l => l.decision === 'pending' && (ok.has(l.child_item) || (!l.child_item && l.child_key))).length;
  const clean = !gaps.length && !noParent.length && !undecided && !openLinks;
  return (
    <div className="di-card">
      <div className="di-card-title">Gaps before you create</div>
      {clean && <div className="di-muted di-small">No gaps — every accepted item has its parent and its children in this import.</div>}
      {gaps.map(g => (
        <details key={g.lv.block_id} className="di-gaprow">
          <summary>
            <b>{g.rows.length}</b> {g.parent.label} with no {g.lv.label}
            {g.lv.must_have && <span className="di-chip di-chip-red">Must have</span>}
          </summary>
          <ul>{g.rows.slice(0, 12).map(p => <li key={p.id}>{p.title}</li>)}
            {g.rows.length > 12 && <li className="di-muted">… and {g.rows.length - 12} more</li>}</ul>
          {!locked && (
            <div className="di-actions">
              <button type="button" className="di-btn di-btn-s" onClick={() => writeGaps(base, g.lv, g.rows.length, g.parent.label, true, reload, setError)}>✨ Write with AI</button>
              <button type="button" className="di-btn di-btn-s" onClick={() => setStep('links')}>Link in Review links</button>
            </div>
          )}
        </details>
      ))}
      {noParent.length > 0 && (
        <div className="di-gaprow"><b>{noParent.length}</b> accepted {noParent.length === 1 ? 'item has' : 'items have'} no parent — created without a trace.
          {' '}<button type="button" className="di-link" onClick={() => setStep('links')}>Link them</button></div>
      )}
      {undecided > 0 && (
        <div className="di-gaprow"><b>{undecided}</b> {undecided === 1 ? 'item is' : 'items are'} not decided — not created.
          {' '}<button type="button" className="di-link" onClick={() => setStep('items')}>Review items</button></div>
      )}
      {openLinks > 0 && (
        <div className="di-gaprow"><b>{openLinks}</b> proposed {openLinks === 1 ? 'link is' : 'links are'} not kept — not written.
          {' '}<button type="button" className="di-link" onClick={() => setStep('links')}>Review links</button></div>
      )}
    </div>
  );
}

function CreateStep({ data, base, setError, items, links, reload, setupId, setStep }) {
  const navigate = useNavigate();
  const { run, levels } = data;
  const [footer, setFooter] = useState(!!run.options?.source_footer);
  const [attach, setAttach] = useState(!!run.options?.attach_file);
  // DHF: {block_id: {docId, parentId, parentLabel}} — where each level's new items go (default: the pool).
  const [placement, setPlacement] = useState({});
  const [newParas, setNewParas] = useState(run.options?.new_paragraphs ?? true);
  const [oldToOrcanos, setOldToOrcanos] = useState(run.options?.old_id_to_orcanos ?? true);
  const [showFieldMap, setShowFieldMap] = useState(() =>
    Object.values(run.options?.fields || {}).some(m => m?.source || m?.origin));
  const [grouping, setGrouping] = useState('');
  const [showOld, setShowOld] = useState(false);
  const suggestFor = async (lv) => {
    setGrouping(lv.block_id);
    try {
      const d = await send(`${base}/items/paragraphs/suggest`, 'POST', { block_id: lv.block_id, parent_label: placement[lv.block_id]?.parentLabel || '' });
      if (d.error) setError(`Part of the items were not grouped: ${d.error}`);
      await reload();
    } catch (e) { setError(e.message); }
    setGrouping('');
  };
  const downloadOldIds = async () => {
    try {
      const d = await api(`/api/build/${setupId}/import-old-ids`);
      downloadCsv('old IDs.csv', [['Type', 'Old ID', 'Orcanos key', 'Name', 'File', 'Waiting for (trace ids not in Orcanos yet)'],
        ...d.rows.map(r => [r.type_label, r.legacy_id, r.orcanos_key, r.name, r.file, (r.refs || []).map(x => x.ref).join(', ')])]);
    } catch (e) { setError(e.message); }
  };
  const saveFields = async (blockId, m) => {
    try { await send(`${base}/options`, 'PATCH', { fields: { [blockId]: m } }); await reload(); } catch (e) { setError(e.message); }
  };
  const creating = run.status === 'creating';
  const accepted = items.filter(i => i.decision === 'accepted');
  const byId = Object.fromEntries(items.map(i => [i.id, i]));
  const okEnd = id => byId[id]?.decision === 'accepted';
  const traces = links.filter(l => l.decision === 'kept' && (okEnd(l.child_item) || (!l.child_item && l.child_key)) && (l.parent_key || okEnd(l.parent_item)));
  const noParent = accepted.filter(i => levels.find(l => l.block_id === i.block_id)?.parent_block_id
    && !['exists', 'update'].includes(i.match) && !traces.some(l => l.child_item === i.id));
  const todo = accepted.filter(i => !DONE.includes(i.create_status));
  const lvById = Object.fromEntries(levels.map(l => [l.block_id, l]));
  // Items the AI wrote rather than took as written (2026-10-27): 49 software items written from a
  // 513(g) PDF went in unnoticed, among 1,160 Excel rows — and were found only by querying the DB.
  const aiMade = levels.map(lv => {
    const mine = todo.filter(i => i.block_id === lv.block_id && !['exists', 'update'].includes(i.match) && ['written', 'derived'].includes(origin(i)));
    const files = [...new Set(mine.map(i => i.sources?.[0]?.file).filter(Boolean))];
    return { lv, n: mine.length, derived: mine.filter(i => origin(i) === 'derived').length, files };
  }).filter(x => x.n);
  // A trace between two projects shows in the matrix only when the filter reads items from all versions (Note #4).
  const crossProject = levels.filter(lv => {
    const p = lvById[lv.parent_block_id];
    return p && lv.project_id && p.project_id && lv.project_id !== p.project_id
      && traces.some(l => (byId[l.child_item]?.block_id || l.child_block) === lv.block_id);
  });
  const downloadList = () => downloadCsv(`import ${String(run.created_at || '').slice(0, 10)}.csv`,
    [['Level', 'Orcanos key', 'Status', 'Old ID', 'Name', 'From file', 'Where in the file', 'How'],
      ...accepted.map(i => [lvById[i.block_id]?.label || i.block_id, i.created_key || i.match_key || '',
        i.create_status || (i.match === 'exists' ? 'link existing' : 'not created yet'), i.doc_ref || '', i.title,
        i.sources?.[0]?.file || '', i.sources?.[0]?.anchor || '', ORIGIN[origin(i)]])]);
  const failed = items.filter(i => ['failed', 'skipped'].includes(i.create_status));
  const linkFailed = links.filter(l => l.write_status === 'failed');
  const tally = run.result?.create;

  const create = async () => {
    const place = {};
    Object.entries(placement).forEach(([bid, v]) => {
      if (v) place[bid] = { doc_id: v.docId, parent_id: v.parentId, parent_label: v.parentLabel };
    });
    try {
      await send(`${base}/create`, 'POST', { source_footer: footer, attach_file: attach, placement: place,
        new_paragraphs: newParas, old_id_to_orcanos: oldToOrcanos });
      reload();
    } catch (e) { setError(e.message); }
  };
  const stop = async () => { try { await send(`${base}/stop`, 'POST'); } catch (e) { setError(e.message); } };

  return (
    <div className="di-two">
      <div className="di-main">
        <div className="di-card di-card-flush">
          <div className="di-scroll-x">
            <table className="di-table">
              <thead><tr><th>Level</th><th>Create</th><th>From</th><th>Link existing</th><th>Update</th><th>Traces</th><th>Done</th></tr></thead>
              <tbody>
                {levels.map(lv => {
                  const acc = accepted.filter(i => i.block_id === lv.block_id);
                  const fresh = acc.filter(i => !['exists', 'update'].includes(i.match));
                  const how = Object.keys(ORIGIN).map(k => [k, fresh.filter(i => origin(i) === k).length]).filter(([, n]) => n);
                  return (
                    <tr key={lv.block_id}>
                      <td><b>{lv.label}</b><div className="di-muted di-small">{lv.is_test ? 'with steps · ' : ''}first workflow state</div></td>
                      <td>{fresh.length}</td>
                      <td className="di-small">{how.length ? how.map(([k, n]) => (
                        <div key={k} className={['written', 'derived'].includes(k) ? 'di-text-amber' : ''}>{n} {ORIGIN_SHORT[k]}</div>)) : '—'}</td>
                      <td>{acc.filter(i => i.match === 'exists').length}</td>
                      <td>{acc.filter(i => i.match === 'update').length}</td>
                      <td>{lv.parent_block_id ? traces.filter(l => (byId[l.child_item]?.block_id || l.child_block) === lv.block_id).length : <span className="di-muted">top level</span>}</td>
                      <td>{acc.filter(i => DONE.includes(i.create_status)).length}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="di-card">
          <div className="di-card-title">On each created item</div>
          <label className="di-check"><input type="checkbox" checked={footer} disabled={creating} onChange={e => setFooter(e.target.checked)} />
            Add the source to the description <span className="di-muted di-small">— e.g. “Source: Product_Spec_v3.docx · 4.2 Heart rate”, and a note when the AI wrote or rewrote it</span></label>
          <label className="di-check"><input type="checkbox" checked={attach} disabled={creating} onChange={e => setAttach(e.target.checked)} />
            Attach the source file to each item <span className="di-muted di-small">— the whole file is uploaded to every item taken from it</span></label>
          <label className="di-check"><input type="checkbox" checked={oldToOrcanos} disabled={creating} onChange={e => setOldToOrcanos(e.target.checked)} />
            Write the old ID to Orcanos <span className="di-muted di-small">— in the item's Migration Reference. The old ID → Orcanos key table is kept here either way, for later imports.</span></label>
          <div className="di-label">Where new items go, per level</div>
          <div className="di-muted di-small">Levels whose items sit in documents can be created under a paragraph; otherwise they go to the pool.</div>
          <label className="di-check"><input type="checkbox" checked={newParas} disabled={creating} onChange={e => setNewParas(e.target.checked)} />
            Create the items' paragraphs under the paragraph I choose <span className="di-muted di-small">— one sub-paragraph per paragraph name; one that exists there already is reused</span></label>
          {levels.map(lv => {
            const mine = accepted.filter(i => i.block_id === lv.block_id && !DONE.includes(i.create_status) && !['exists', 'update'].includes(i.match));
            const groups = Object.entries(mine.reduce((m, i) => { const k = i.paragraph || ''; m[k] = (m[k] || 0) + 1; return m; }, {}))
              .sort((a, b) => (a[0] === '') - (b[0] === '') || a[0].localeCompare(b[0]));
            const place = placement[lv.block_id];
            return (
              <div key={lv.block_id} className="di-place-level">
                <DocPlacement title={lv.label} setupId={setupId} levelNum={lv.depth}
                  blockId={lv.parent_block_id ? lv.block_id : ''} disabled={creating}
                  onChange={v => setPlacement(p => ({ ...p, [lv.block_id]: v }))} />
                {place && mine.length > 0 && (
                  <div className="di-paras">
                    <div className="di-card-row">
                      <span className="di-small"><b>{mine.length}</b> new {lv.label} under <b>{place.parentLabel}</b>
                        {newParas && groups.some(([k]) => k) ? ` in ${groups.filter(([k]) => k).length} paragraphs` : ''}</span>
                      {!creating && mine.length > 1 && (
                        <button type="button" className="di-btn di-btn-s" disabled={!!grouping} onClick={() => suggestFor(lv)}
                          title="One AI call groups these items into paragraphs under the one you chose; the paragraphs that exist there are reused">
                          {grouping === lv.block_id ? 'Grouping…' : '✨ Suggest paragraphs'}</button>
                      )}
                    </div>
                    {newParas && (
                      <div className="di-chips">
                        {groups.slice(0, 40).map(([k, n]) => (
                          <span key={k || '-'} className={`di-chip ${k ? 'di-chip-level' : ''}`}>{k ? `¶ ${k}` : `directly under ${place.parentLabel}`} · {n}</span>))}
                        {groups.length > 40 && <span className="di-muted di-small">+{groups.length - 40} more</span>}
                      </div>
                    )}
                    {newParas && !groups.some(([k]) => k) && mine.length > 40 && (
                      <div className="di-warn">{mine.length} items under one paragraph — consider ✨ Suggest paragraphs.</div>)}
                  </div>
                )}
              </div>
            );
          })}
          {/* Optional, folded away: only for a tenant with its own "source" / "AI generated" field. The
              description note above and Download the list cover everyone else. Open when one is saved. */}
          <button type="button" className="di-link" onClick={() => setShowFieldMap(v => !v)}>
            {showFieldMap ? '▾' : '▸'} Advanced: write the source or an AI mark into an Orcanos field</button>
          {showFieldMap && (
            <>
              <div className="di-muted di-small">Optional. Only if your work item type has a field for it — e.g. a text field
                <i> Source</i> gets “Requirements.xlsx · row 12”, or a field <i>AI generated</i> gets “Written by AI”.
                Most tenants leave this empty; the description note above already records the source.</div>
              {levels.map(lv => (
                <FieldMap key={lv.block_id} lv={lv} base={base} mapping={(run.options?.fields || {})[lv.block_id]}
                  onSave={saveFields} setError={setError} locked={creating} />
              ))}
            </>
          )}
        </div>
        {aiMade.length > 0 && (
          <div className="di-card di-card-warn">
            <div className="di-card-title">Not in your files as written</div>
            <div className="di-small">These items were written by the AI, not taken from a sheet row or quoted from a document. Check them on <button type="button" className="di-link" onClick={() => setStep('items')}>Review items</button> — or reject them there — before you create.</div>
            <ul className="di-fails">
              {aiMade.map(x => (
                <li key={x.lv.block_id}><b>{x.n} {x.lv.label}</b> — {x.derived ? `${x.derived} derived from another level's documents${x.n > x.derived ? ', ' : ''}` : ''}
                  {x.n > x.derived ? `${x.n - x.derived} written from ${x.files.join(', ') || 'the documents'}` : ''}</li>))}
            </ul>
          </div>
        )}
        {crossProject.length > 0 && (
          <div className="di-card di-card-warn">
            <div className="di-card-title">Traces between two projects</div>
            {crossProject.map(lv => (
              <div key={lv.block_id} className="di-small">{lvById[lv.parent_block_id].label} → {lv.label}: the two levels are in different Orcanos projects.</div>))}
            <div className="di-muted di-small">Orcanos keeps these traces, but the matrix shows them only when the level's filter has its <b>Traced Items Info</b> column set to <b>items from all versions</b>. Check that filter in Orcanos, or the new items look untraced here.</div>
          </div>
        )}
        <GapSummary levels={levels} items={items} links={links} accepted={accepted} noParent={noParent} base={base}
          reload={reload} setError={setError} setStep={setStep} locked={creating || run.status === 'extracting'} />
        {creating && (
          <div className="di-card">
            <div className="di-card-row"><b>{run.phase}</b>{!/^Setting /.test(run.phase || '') && <span className="di-muted di-small">top level first — a trace needs both items</span>}</div>
            <div className="di-bar"><i style={{ width: `${run.progress || 0}%` }} /></div>
            <button type="button" className="di-btn" onClick={stop}>Stop after the current item</button>
            <div className="di-muted di-small">You can leave this page — the run continues.</div>
          </div>
        )}
        {tally && !creating && (
          <div className="di-card">
            <div className="di-card-title">Result</div>
            <div className="di-chips">
              <span className="di-chip di-chip-ok">{tally.created} created</span>
              <span className="di-chip di-chip-purple">{tally.existing} existing linked</span>
              {tally.updated > 0 && <span className="di-chip di-chip-amber">{tally.updated} updated</span>}
              {tally.attached > 0 && <span className="di-chip">{tally.attached} files attached</span>}
              <span className="di-chip di-chip-ok">{tally.traces} traces</span>
              {tally.failed > 0 && <span className="di-chip di-chip-red">{tally.failed} failed</span>}
              {tally.not_linked > 0 && <span className="di-chip di-chip-amber">{tally.not_linked} traces not written</span>}
              {tally.skipped > 0 && <span className="di-chip di-chip-amber">{tally.skipped} skipped</span>}
            </div>
            {run.error && <div className="di-error">{run.error}</div>}
            {(failed.length > 0 || linkFailed.length > 0) && (
              <ul className="di-fails">
                {failed.map(i => <li key={i.id}><b>{i.title}</b> — {i.create_error}</li>)}
                {linkFailed.map(l => <li key={l.id}><b>{l.child_name}</b> → {l.parent_name || l.parent_key} — {l.write_error}</li>)}
              </ul>
            )}
          </div>
        )}
        {tally && !creating && (
          <SetFieldAfter levels={levels} items={items} base={base} after={run.result?.after} reload={reload} setError={setError} />
        )}
      </div>
      <aside className="di-side">
        <div className="di-card-title">Ready to create</div>
        <div className="di-stats">
          <div><b>{todo.filter(i => !['exists', 'update'].includes(i.match)).length}</b><span>new items</span></div>
          <div><b>{traces.filter(l => l.write_status !== 'written').length}</b><span>traces</span></div>
          <div><b>{todo.filter(i => i.match === 'exists').length}</b><span>existing reused</span></div>
          {todo.some(i => i.match === 'update') && <div><b>{todo.filter(i => i.match === 'update').length}</b><span>to update</span></div>}
          <div className={noParent.length ? 'di-stat-red' : ''}><b>{noParent.length}</b><span>without a parent</span></div>
        </div>
        <div className="di-muted di-small">Items not decided, rejected items and removed links are not written. The import keeps every decision and who made it.</div>
        {!creating && (
          <button type="button" className="di-btn di-btn-p" disabled={!todo.length && !traces.some(l => l.write_status !== 'written')} onClick={create}>
            {run.status === 'done' && tally ? 'Create what is missing' : `Create ${todo.length} in Orcanos`}</button>
        )}
        <button type="button" className="di-link" onClick={() => navigate(`/trace/matrix/${setupId}`)}>Open the panel</button>
        {accepted.length > 0 && (
          <button type="button" className="di-link" onClick={downloadList}
            title="Every accepted item: level, Orcanos key, status, old ID, name, the file and place it came from, and whether it is an Excel row, quoted or written by the AI">
            Download the list of items</button>)}
        <div className="di-card-title">Old IDs</div>
        <div className="di-muted di-small">{data.old_ids
          ? `${data.old_ids} old IDs of this panel's item types are saved with their Orcanos key — a later import of the level above or below traces through them.`
          : 'Items with an old ID are saved with their Orcanos key when created, so a later import can trace to them.'}</div>
        {data.old_ids > 0 && <button type="button" className="di-link" onClick={() => setShowOld(true)}>Show old ID → Orcanos</button>}
        {data.old_ids > 0 && <button type="button" className="di-link" onClick={downloadOldIds}>Download the old-ID table</button>}
      </aside>
      {showOld && <OldIdsPopup setupId={setupId} onClose={() => setShowOld(false)} />}
    </div>
  );
}

// ------------------------------------------------------------------ old ID → Orcanos key
const OLD_PAGE_SIZES = [25, 50, 100, 200];

/** The saved old ID → Orcanos key table (doc_import_legacy), one tab per work item type, paged. */
function OldIdsPopup({ setupId, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  useEffect(() => {
    api(`/api/build/${setupId}/import-old-ids`)
      .then(d => { setRows(d.rows || []); setType((d.rows || [])[0]?.type_code || ''); })
      .catch(e => setErr(e.message));
  }, [setupId]);
  useEffect(() => {
    const esc = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  useEffect(() => { setPage(0); }, [type, q, size]);
  const types = [...new Map((rows || []).map(r => [r.type_code, r.type_label])).entries()];
  const needle = q.trim().toLowerCase();
  const shown = (rows || []).filter(r => r.type_code === type && (!needle
    || [r.legacy_id, r.orcanos_key, r.name, r.file].some(v => String(v || '').toLowerCase().includes(needle))));
  const pages = Math.max(1, Math.ceil(shown.length / size));
  const at = Math.min(page, pages - 1);
  const slice = shown.slice(at * size, (at + 1) * size);
  return (
    <div className="di-pop-back" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="di-side di-item-panel--big di-old-pop" role="dialog" aria-modal="true">
        <div className="di-panel-head">
          <div className="di-card-title">Old ID → Orcanos</div>
          <button type="button" className="di-link" onClick={onClose}>✕ Close</button>
        </div>
        {err && <div className="di-error">{err}</div>}
        {!rows && !err && <div className="di-muted">Loading…</div>}
        {rows && !rows.length && <div className="di-muted">No old IDs saved yet.</div>}
        {rows && rows.length > 0 && <>
          <div className="di-tabs">
            {types.map(([code, label]) => (
              <button key={code} type="button" className={code === type ? 'on' : ''} onClick={() => setType(code)}>
                {label} <span className="di-muted di-small">{rows.filter(r => r.type_code === code).length}</span></button>))}
          </div>
          <input className="di-old-search" placeholder="Search old ID, key, name or source" value={q} onChange={e => setQ(e.target.value)} />
          <div className="di-scroll-x">
            <table className="di-table">
              <thead><tr><th>Old ID</th><th>Orcanos</th><th>Name</th><th>Source</th></tr></thead>
              <tbody>
                {slice.map(r => (
                  <tr key={`${r.type_code}:${r.legacy_norm}`}>
                    <td>{r.legacy_id}</td>
                    <td className="di-nowrap">{r.url
                      ? <a href={r.url} target="_blank" rel="noreferrer">{r.orcanos_key}</a> : r.orcanos_key}</td>
                    <td>{r.name}</td>
                    <td className="di-small">{r.file || <span className="di-muted">—</span>}</td>
                  </tr>))}
                {!slice.length && <tr><td colSpan={4} className="di-muted">Nothing matches.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="di-old-pager">
            <span className="di-muted di-small">{shown.length ? `${at * size + 1}–${at * size + slice.length} of ${shown.length}` : '0'}</span>
            <select value={size} onChange={e => setSize(Number(e.target.value))}>
              {OLD_PAGE_SIZES.map(n => <option key={n} value={n}>{n} rows</option>)}
            </select>
            <button type="button" className="di-btn" disabled={at === 0} onClick={() => setPage(0)}>«</button>
            <button type="button" className="di-btn" disabled={at === 0} onClick={() => setPage(at - 1)}>‹</button>
            <span className="di-small">Page {at + 1} of {pages}</span>
            <button type="button" className="di-btn" disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>›</button>
            <button type="button" className="di-btn" disabled={at >= pages - 1} onClick={() => setPage(pages - 1)}>»</button>
          </div>
        </>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ the run
function ImportRun({ setupId, importId }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromDhf = params.get('from') === 'dhf';
  const base = `/api/build/${setupId}/import/${importId}`;
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [links, setLinks] = useState([]);
  const [step, setStep] = useState(null);
  const [error, setError] = useState(null);
  const lastStatus = useRef(null);
  const lastStamp = useRef(null);
  const lastLists = useRef(0);

  const loadReview = useCallback(async () => {
    const [i, l] = await Promise.all([api(`${base}/items`), api(`${base}/links`)]);
    setItems(i.items);
    setLinks(l.links);
  }, [base]);

  const reload = useCallback(async () => {
    try {
      const d = await api(base);
      setData(d);
      const st = d.run.status;
      // The run's stamp moves when the documents are read again — new item ids — so the lists
      // are re-read then too, not only on a status change (a stale id 409s every action).
      const stampMoved = lastStamp.current !== null && lastStamp.current !== d.run.updated_at;
      lastStamp.current = d.run.updated_at;
      if (lastStatus.current === st && stampMoved && ['review', 'done'].includes(st)) await loadReview();
      if (lastStatus.current !== st) {
        const prev = lastStatus.current;
        if (['review', 'done', 'creating'].includes(st)) await loadReview();
        if (prev === null) setStep(st === 'review' ? 'items' : (st === 'done' || st === 'creating') ? 'create' : 'docs');
        else if (prev === 'extracting' && ['review', 'done'].includes(st)) setStep('items');
        lastStatus.current = st;
      } else if (st === 'creating' && Date.now() - lastLists.current > 15000) {
        // The full lists are megabytes on a big run — every 1.5 s froze the site (Note #99)
        lastLists.current = Date.now();
        await loadReview();
      }
    } catch (e) { setError(e.message); }
  }, [base, loadReview]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {                       // another tab may have read the documents again; a hidden
    const onVis = () => { if (document.visibilityState === 'visible') reload(); };   // tab's poll is throttled
    window.addEventListener('focus', reload);
    document.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('focus', reload); document.removeEventListener('visibilitychange', onVis); };
  }, [reload]);
  const fail = useCallback((msg) => {     // STALE (doc_import_api.py): the server's ids moved on
    setError(msg);
    if (msg && msg.startsWith('This list is out of date')) loadReview().catch(() => {});
  }, [loadReview]);
  const busy = data && BUSY.includes(data.run.status);
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(reload, 1500);
    return () => clearInterval(t);
  }, [busy, reload]);

  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState('');
  const saveName = async () => {
    try { await send(base, 'PATCH', { name }); setEditingName(false); reload(); } catch (e) { setError(e.message); }
  };

  if (!data) return <div className="di-page">{error ? <div className="di-error">{error}</div> : <div className="di-muted">Loading…</div>}</div>;
  const st = data.run.status;
  const reviewable = ['review', 'done', 'creating'].includes(st);
  const steps = [['docs', '1 Documents', true], ['items', '2 Review items', reviewable],
    ['links', '3 Review links', reviewable], ['create', '4 Create', reviewable]];
  const order = steps.map(s => s[0]);

  return (
    <div className="di-page">
      <div className="di-head">
        <div className="di-head-title">
          <button type="button" className="di-back" onClick={() => navigate(`/trace/build/${setupId}/import${fromDhf ? '?from=dhf' : ''}`)}>← Imports</button>
          <div>
            <div className="di-muted di-small">AI Studio · {data.panel_name}</div>
            {editingName ? (
              <span className="di-name-edit">
                <input aria-label="Import name" value={name} onChange={e => setName(e.target.value)} autoFocus />
                <button type="button" className="di-btn di-btn-s" onClick={saveName}>Save</button>
              </span>
            ) : (
              <h1 onDoubleClick={() => { setName(data.run.name || ''); setEditingName(true); }} title="Double-click to rename">
                {data.run.name || 'Import from documents'}</h1>
            )}
          </div>
        </div>
        <nav className="di-stepper" aria-label="Import steps">
          {steps.map(([k, t, ok], i) => (
            <React.Fragment key={k}>
              {i > 0 && <span className="di-muted">—</span>}
              <button type="button" disabled={!ok}
                className={`di-stepchip ${step === k ? 'on' : order.indexOf(k) < order.indexOf(step) ? 'past' : ''}`}
                onClick={() => setStep(k)}>{t}</button>
            </React.Fragment>
          ))}
        </nav>
      </div>
      {error && <div className="di-error" role="alert">{error} <button type="button" className="di-link" onClick={() => setError(null)}>Dismiss</button></div>}
      {(data.run.result?.errors || []).length > 0 && step !== 'docs' && (
        <details className="di-warn"><summary>{data.run.result.errors.length} part(s) could not be read or checked</summary>
          <ul>{data.run.result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul></details>
      )}
      {(data.run.result?.truncated_pools || []).length > 0 && step !== 'docs' && (
        <div className="di-warn">Only the first 800 existing items of {data.run.result.truncated_pools.join(', ')} were checked for duplicates.</div>
      )}
      {BUSY.includes(st) && st !== 'creating' && step !== 'docs' && (
        <div className="di-card di-progress-box">
          <div className="di-small">{data.run.phase || 'Working…'}</div>
          <div className="di-bar"><i style={{ width: `${data.run.progress || 0}%` }} /></div>
          <button type="button" className="di-btn di-btn-s" onClick={() => send(`${base}/stop`, 'POST').catch(e => setError(e.message))}>Stop</button>
        </div>
      )}
      {step === 'docs' && <DocumentsStep data={data} base={base} reload={reload} setError={setError} />}
      {step === 'items' && <ItemsStep data={data} base={base} setError={fail} items={items} setItems={setItems}
        reloadItems={() => loadReview().catch(e => setError(e.message))} />}
      {step === 'links' && <LinksStep data={data} base={base} setError={fail} items={items} links={links} setLinks={setLinks}
        reloadLinks={() => api(`${base}/links`).then(l => setLinks(l.links)).catch(e => setError(e.message))}
        reloadAll={() => loadReview().catch(e => setError(e.message))} reload={reload} />}
      {step === 'create' && <CreateStep data={data} base={base} setError={fail} items={items} links={links} reload={reload} setupId={setupId} setStep={setStep} />}
    </div>
  );
}

export default function DocImport() {
  const { setupId, importId } = useParams();
  return importId ? <ImportRun setupId={setupId} importId={importId} /> : <ImportList setupId={setupId} />;
}
